// Blip's world tools (docs/BLIP_WORLD_EDITOR_DESIGN.md, "Tools"): he sees and
// changes the world open in the World Editor through the editor's request
// handler (_release/samples/world-editor/editor_requests.bb), whose requests
// go through the run frame's page channel (runner.js request). Their
// definitions, as Claude sees them, are in web/ide/blip/tools.json.
//
// Each of Blip's requests is one step of the editor's: begin marks it, and
// finish folds his edits into one undo step and returns what he made, for
// the change card, whose Undo takes back his change alone (undo).

import { describeGlb, readGlb } from "./glb.js";
import { MAP_SUFFIXES } from "./texture_names.js";

const PROTOCOL = "blitz3d.editor-request";
const NOT_OPEN = "The World Editor is not open. The user can open it with the World Editor button, or ask you to " +
  "open it (open_world_editor).";
const MAX_OPERATIONS = 200;
const MAX_PICTURES = 4;
const PICTURE_WIDTH = 1024;
// How long the editor may take: physics colliders are cooked over some
// frames, and a request waits while the user drags.
const ANSWER_MS = 30000;
// An interior's build: the IDE compiles and runs its program, which may carve
// some hundreds of shapes (docs/BLIP_INTERIORS_DESIGN.md).
const INTERIOR_MS = 900000;
// Opening Maplet+: the editor waits up to 30 seconds for the IDE to start it.
const MAPLET_MS = 45000;
// Each run of the editor is first asked hello, which it answers at once even
// while it is busy. One that does not answer is not asked again for a while.
const HELLO_MS = 5000;
const DEAF_MS = 20000;
const DEAF = "The World Editor is not answering. If it has only just opened, try again in a moment. If it still does " +
  "not answer, this site's World Editor may be older than its IDE, and the world tools cannot reach it until the site " +
  "is updated.";

export class WorldToolError extends Error {}

// The kinds of asset a project file becomes, by its extension, that are added
// to a world with a path of its own (importFile); the rest the editor's own
// import adds.
const ASSET_KINDS = { glb: "model", gltf: "model", b3d: "model", "3ds": "model", x: "model", png: "texture", jpg: "texture",
  jpeg: "texture", bmp: "texture", tga: "texture", dds: "texture", wav: "sound", ogg: "sound" };

// A project file's path from a world's folder, as the world format keeps
// paths on every platform: forward slashes, .. for each folder up. A new
// world, not saved yet, is in the project's folder.
export function pathFromWorld(world, file) {
  const from = world ? world.split("/").slice(0, -1) : [];
  const to = file.split("/");
  let common = 0;
  while (common < from.length && common < to.length - 1 && from[common].toLowerCase() === to[common].toLowerCase()) ++common;
  return [...from.slice(common).map(() => ".."), ...to.slice(common)].join("/");
}

const plural = (count, one, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;
// A number as people write it: up to three decimals, no trailing zeros.
const number = (value) => typeof value === "number" && Number.isFinite(value) ? String(+value.toFixed(3)) : "?";
const vector = (values) => Array.isArray(values) ? values.map(number).join(", ") : "";
const isVector = (value) => Array.isArray(value) && value.length === 3 && value.every((part) => typeof part === "number" &&
  Number.isFinite(part));

// The points and triangles a fitted hull or mesh keeps in a .b3collider file
// beside the world, and whether the user's Save has written it.
function colliderData(data) {
  const counts = ["points", "triangles"].filter((key) => typeof data[key] === "number")
    .map((key) => `${data[key].toLocaleString("en")} ${key}`);
  return `${counts.length ? `${counts.join(" and ")} in ` : ""}${data.file}` +
    (data.saved === false ? " (not saved yet: the user's Save writes it)" : "");
}

// A physics object's collider: its kind, and a fitted one's data.
function colliderText(collider) {
  const kind = collider.kind === "shape" ? "the object's own shape" : `a ${collider.kind || "collider of its own"}`;
  return `collider: ${kind}${collider.file ? `, ${colliderData(collider)}` : ""}`;
}

// What a top view covers, as world_look says it: "x runs from -20 (left)
// to 20 (right), z from 12.5 (top) to -12.5 (bottom)".
export function areaText(area) {
  return `x runs from ${number(area.x0)} (left) to ${number(area.x1)} (right), z from ${number(area.z0)} (top) to ` +
    `${number(area.z1)} (bottom)`;
}

// One object's line in a summary of the world.
export function objectLine(object) {
  const parts = [`${object.id} "${object.name}" ${(object.kind || "object").toLowerCase()}`];
  const extra = (object.components || []).slice(1);
  if (extra.length) parts[0] += ` (with ${extra.join(", ")})`;
  // A light's cookie: its built-in pattern, or "picture" and its texture asset.
  if (object.cookie) parts.push(`cookie ${object.cookie}`);
  if (object.parent) parts.push(`under ${object.parent}`);
  parts.push(`at ${vector(object.position)}`);
  if (Array.isArray(object.rotation) && object.rotation.some((angle) => Math.abs(angle) > 0.0005)) {
    parts.push(`turned ${vector(object.rotation)}`);
  }
  if (Array.isArray(object.scale) && object.scale.some((scale) => Math.abs(scale - 1) > 0.0005)) {
    parts.push(`scale ${vector(object.scale)}`);
  }
  if (object.world) parts.push(`(in the world at ${vector(object.world)})`);
  if (Array.isArray(object.size)) parts.push(`size ${object.size.map(number).join(" x ")}`);
  // A zone: its region, and the look, camera effects and sounds it brings in.
  if (typeof object.radius === "number") parts.push(`radius ${number(object.radius)}`);
  if (object.regionOf) parts.push(regionZoneText(object));
  if (object.look) parts.push(`look ${object.look}`);
  if (object.effects) parts.push(`camera effects ${object.effects}`);
  if (object.soundscape) parts.push(`soundscape ${object.soundscape}`);
  // The ground: a path that follows a terrain's, an object that levels it,
  // and a terrain's size, layers and regions.
  if (object.ground) {
    parts.push(`${{ points: "its points rest on", whole: "lies on", shape: "runs on a shelf it shapes in" }[object.ground] ||
      "follows"} the ground of ${object.groundOf || "a terrain"} (follow ${object.ground})`);
  }
  if (object.levels) {
    parts.push(object.shaping === "clear" ? `keeps the trees and rocks of ${object.levels} off it` :
      `${{ dig: "digs", raise: "builds up" }[object.shaping] || "levels"} the ground of ${object.levels} under it`);
  }
  if (object.terrain && typeof object.terrain === "object") parts.push(terrainLine(object.terrain));
  if (object.collider && typeof object.collider === "object") parts.push(colliderText(object.collider));
  // Baked lighting: how it takes part, and why; and whether a glowing
  // object's glow lights the world in bakes.
  if (object.lighting) parts.push(`lighting ${object.lighting}${object.lightingReason ? ` (${object.lightingReason})` : ""}`);
  if (object.glow) parts.push(`its glow ${object.glow}`);
  // A model's triangles, the simpler versions drawn far away, and the one
  // the views preview (lod-view).
  if (typeof object.triangles === "number") parts.push(`${object.triangles} triangles`);
  if (object.lod) parts.push(`level of detail ${object.lod}`);
  if (typeof object.lodView === "number") {
    parts.push(`the views show ${object.lodView ? `its simpler version ${object.lodView}` : "its full detail"} at any distance`);
  }
  // How walking characters treat it (world_action navigation).
  if (object.walking && typeof object.walking === "object") parts.push(walkingText(object.walking));
  // A walking character's job, and what it walks as.
  if (object.job) parts.push(object.job);
  if (object.walksAs) parts.push(object.walksAs.charAt(0).toLowerCase() + object.walksAs.slice(1));
  // A crowd (world_action crowd): who, how many and what they do, and how
  // the views' people are getting on.
  if (typeof object.crowd === "string") parts.push(crowdText(object));
  // An interior (world_action interior): the program that builds it, and the
  // Maplet+ project it is shaped in (world_action maplet).
  if (object.interior && typeof object.interior === "object" && object.interior.program) {
    parts.push(`an interior built by ${object.interior.program}` +
      (object.interior.project ? `, shaped in Maplet+ as ${object.interior.project}` : ""));
  }
  // Its switches, doors, timers and spawners (world_action behaviour), by
  // component id, and what Problems says about them.
  if (Array.isArray(object.gameplay)) {
    for (const entry of object.gameplay) {
      parts.push(`${entry.kind} ${entry.id}: ${entry.does}${entry.opens ? `, opening ${entry.opens}` : ""}` +
        `${entry.openedBy ? `, opened by ${entry.openedBy}` : ""}${entry.enabled === false ? ", switched off" : ""}`);
    }
  }
  if (Array.isArray(object.gameplayProblems) && object.gameplayProblems.length) {
    parts.push(`problems: ${object.gameplayProblems.join("; ")}`);
  }
  if (object.visible === false) parts.push("hidden");
  if (object.enabled === false) parts.push("disabled");
  if (object.locked) parts.push("locked");
  if (object.keepLoaded) parts.push("keep loaded");
  return parts.join(", ");
}

// A terrain's line: its cells and their size, its tiles when it is tiled, its
// ground's range in the world, its ground layers (or ground types) and its
// region maps.
function terrainLine(terrain) {
  const cells = terrain.cells || 0, cell = terrain.cell || 1, bottom = terrain.bottom || 0;
  const tiles = terrain.tiles ? `, ${terrain.tiles} x ${terrain.tiles} tiles (${terrain.tilesLoaded || 0} loaded in full)` : "";
  const parts = [`a terrain of ${cells} x ${cells} cells ${number(cell)} across (${number(cells * cell)} wide)${tiles}, its ground ` +
    `from ${number(bottom)} to ${number(bottom + (terrain.height || 0))}`];
  if (Array.isArray(terrain.groundTypes)) {
    parts.push(terrain.groundTypes.length ? `ground types ${terrain.groundTypes.join(", ")}` : "no ground types");
  } else {
    parts.push(Array.isArray(terrain.layers) && terrain.layers.length ? `ground layers ${terrain.layers.join(", ")}` :
      (terrain.tiles ? "no ground types" : "no ground layers"));
  }
  if (Array.isArray(terrain.regionMaps) && terrain.regionMaps.length) {
    parts.push(`region maps ${terrain.regionMaps.map((map) => `${map.name} (${(map.regions || []).join(", ") ||
      "no regions"})`).join("; ")}`);
  }
  return parts.join(", ");
}

// Who is signed in to Blitz3D+ on this computer (editor_account.bb), as
// world_state and world_action account say.
function accountLine(account, said) {
  if (!account) return said || "The World Editor did not say who is signed in.";
  const where = account.store ? ` The session is kept in ${account.store}.` : "";
  const state = account.signingIn ? "The browser is open for the user to sign in with their passkey and allow this computer; " +
    "the World Editor waits for them (account cancel stops it)." : account.signedIn ? `This computer is signed in to Blitz3D+ as ` +
    `${account.name || "the user"}: the IDE, the World Editor, Blip and AI assistants share it.${where}` :
    "No one is signed in to Blitz3D+ on this computer (account sign-in signs them in).";
  return `${said ? `${said.replace(/[.!]?$/, ".")} ` : ""}${state}`;
}

// The world's summary, from a state answer.
export function worldSummary(state) {
  const world = state.world || {};
  const lines = [`World: ${world.path || "a new world (not saved yet)"}${world.name ? ` ("${world.name}")` : ""}, ` +
    `revision ${world.revision}${world.unsaved ? ", unsaved changes" : ""}${world.readOnly ? `, read-only: ${world.readOnly}` : ""}` +
    // Edits, Undo and Save go on, keeping the files' paths; Play refuses.
    `${world.missingFiles ? `, ${plural(world.missingFiles, "asset file")} missing (Play refuses until they are back)` : ""}`];
  lines.push(`Selected: ${(state.selection || []).join(", ") || "nothing"}`);
  // With no IDE, the editor runs Test's programs itself (editor_host.bb).
  if (state.host === "editor") lines.push("Test: no IDE opened this World Editor, so it runs Test's programs itself" +
    `${state.debug ? ", with the Blitz debugger (Debug is ticked in Test's program menu)" : ""}; the Output page below the views shows what they print.`);
  if (state.account) lines.push(`Account: ${state.account.signingIn ? "signing in, in the user's browser" :
    state.account.signedIn ? `signed in to Blitz3D+ as ${state.account.name || "the user"} (the Account button)` : "not signed in"}.`);
  if (typeof state.output === "string" && state.output.trim())
    lines.push(`Output page, last lines:\n${state.output.trim().split("\n").slice(-12).join("\n")}`);
  if (world.live) lines.push(`DebugWorld: game ${world.paused ? "paused" : "running"}.`);
  // Whether Play Online Test can play the world now, and why not.
  if (state.onlineTest) lines.push(`Play Online Test: ${state.onlineTest.available ? "can play this world" :
    `not now: ${state.onlineTest.why || "unavailable"}`}`);
  if (state.focus) {
    const camera = state.camera;
    lines.push(`Focus point: ${vector(state.focus)}.${camera ? ` Editor camera at ${vector(camera.position)}, looking at ` +
      `${vector(camera.target)}${camera.projection === "orthographic" ? " (orthographic)" : ""}` : ""}`);
  }
  const objects = state.objects || [];
  lines.push(`Objects (${state.matched ?? objects.length}${state.truncated ? `, ${objects.length} shown` : ""}):`);
  for (const object of objects) lines.push(objectLine(object));
  if (state.truncated) lines.push(`... and ${state.matched - objects.length} more: ask for those near a point, or by ids.`);
  const assets = state.assets || [];
  lines.push(`Assets (${assets.length}): ${assets.map((asset) => `${asset.id} (${asset.kind}` +
    `${typeof asset.triangles === "number" ? `, ${asset.triangles} triangles` : ""}` +
    // A model Prepare Model saved, and the levels of detail its file keeps.
    `${asset.preparedFrom ? `, prepared from ${asset.preparedFrom}` : ""}` +
    `${typeof asset.lodLevels === "number" ? `, ${plural(asset.lodLevels, "level")} of detail in its file` : ""}` +
    `${asset.texture ? `, textured with ${asset.texture}` : ""}` +
    `${asset.layers?.length ? `, overlay: ${asset.layers.join(", ")}` : ""}` +
    // A Classic or shader material's maps (material-map).
    `${Array.isArray(asset.maps) && asset.maps.length ? `, maps: ${asset.maps.map(materialMap).join("; ")}` : ""}` +
    // A prefab a spawner can make (make-spawner).
    `${asset.spawnable === true ? ", spawnable" : asset.spawnable === false ? ", not spawnable" : ""}` +
    // Its file is missing or cannot be read: the editor shows a placeholder.
    `${asset.problem ? `, not loaded: ${asset.problem}` : ""}${lostFile(asset)}` +
    // A resource's own files that are missing, such as a material's picture.
    `${Array.isArray(asset.missingFiles) && asset.missingFiles.length ? `, its ${asset.missingFiles.length === 1 ?
      "file" : "files"} ${asset.missingFiles.join(", ")} missing` : ""})`).join(", ") || "none"}`);
  if (state.settings && typeof state.settings === "object") lines.push(`Settings: ${JSON.stringify(state.settings)}`);
  if (state.soundscape && typeof state.soundscape === "object") lines.push(soundscapeLine(state.soundscape));
  if (state.lighting && typeof state.lighting === "object") lines.push(lightingLine(state.lighting));
  if (state.photo && (state.photo.live || state.photo.timelapse?.available || state.photo.state !== "idle")) lines.push(photoLine(state.photo));
  if (state.loading && typeof state.loading === "object") lines.push(loadingLine(state.loading));
  if (state.navigation && typeof state.navigation === "object") lines.push(navigationLine(state.navigation));
  if (Array.isArray(state.crowds) && state.crowds.length) lines.push(crowdsLine(state.crowds));
  // Blip's recipes: those saved beside the world, and those placed in it.
  if (state.recipes && typeof state.recipes === "object") {
    const line = recipesLine(state.recipes);
    if (line) lines.push(line);
  }
  if (state.assistants && typeof state.assistants === "object") {
    const line = assistantsLine(state.assistants);
    if (line) lines.push(line);
  }
  const tab = state.environmentTab;
  if (tab && typeof tab === "object") {
    lines.push(`Environment tab: ${tab.asset || "a new environment, not saved yet"}, its preview on the ` +
      `${PREVIEW_SCENES[tab.scene] || tab.scene} scene${tab.unsaved ? ", unsaved changes" : ""}`);
  }
  return lines.join("\n");
}

// Blip and AI assistants in the user's views (editor_assistants.bb): where
// each one is, or that View > Blip and AI Assistants hides them.
export function photoLine(photo) {
  return `Photo: ${photo.state}, ${photo.stage || "waiting"}, ${Math.round((photo.progress || 0) / 10)}%` +
    `${photo.width ? `, ${photo.width} × ${photo.height}` : ""}${photo.file ? `; file: ${photo.file}` : ""}` +
    `${photo.error ? `; ${photo.error}` : ""}${photo.notes ? `. ${photo.notes}` : ""}` +
    `${photo.live ? photo.available ? ". DebugWorld capture pauses the game; choose Continue afterwards." :
      ". DebugWorld capture unavailable: rebuild the game with this version of Blitz3D+." : ""}` + timelapseLine(photo.timelapse);
}

export function timelapseLine(sequence) {
  if (!sequence) return "";
  if (!sequence.available && !sequence.folder) return ". Timelapse unavailable: desktop editor and an updated game runtime are required.";
  if (!sequence.folder) return ". Desktop timelapse is available.";
  return `\nTimelapse: ${sequence.active ? sequence.ending ? "ending" : "recording" : "ended"}, ${sequence.frames || 0} photos; folder: ${sequence.folder}.` +
    (sequence.fps > 0 ? ` Playback: ${((sequence.frames || 0) / sequence.fps).toFixed(1)} seconds at ${sequence.fps} fps.` : "") +
    (sequence.active ? " Only the recording viewport and photo settings are locked; other viewports remain independent." +
      (sequence.activeViewLocked === false ? " The active viewport can be framed." : " The active viewport is recording; select another viewport to frame.") +
      (sequence.physicsPreview ? " Physics preview records one step per photo at 60 fps." : "") +
      (sequence.waitingForPreview ? " Waiting for the updated preview; recording will continue with the same camera and settings." : "") +
      " end-timelapse stops and keeps completed PNGs." : "") +
    (sequence.error ? ` ${sequence.error}` : "") +
    (sequence.exporting ? ` Video: exporting, ${Math.round((sequence.exportProgress || 0) / 10)}%.` :
      sequence.exportError ? ` Video: failed, ${sequence.exportError}` :
      sequence.exportProgress === 1000 ? ` Video: saved, ${sequence.video}.` :
      sequence.videoAvailable ? ` Video export available (MJPEG AVI, no audio, at most 2 GB); default ${sequence.fps} fps.` : "");
}

function assistantsLine(assistants) {
  if (assistants.shown === false) return "In the views: Blip and AI assistants are hidden (View > Blip and AI Assistants is off).";
  const PLACES = { working: "working", thinking: "thinking", finishing: "just finished", resting: "resting beside the user's view",
    asleep: "asleep beside the user's view" };
  const shown = (Array.isArray(assistants.list) ? assistants.list : []).filter((entry) => entry && PLACES[entry.state]);
  if (!shown.length) return "";
  // A buddy's Blip rests beside that person's view.
  const place = (entry) => entry.owner && (entry.state === "resting" || entry.state === "asleep") ?
    PLACES[entry.state].replace("the user's", `${entry.owner}'s`) : PLACES[entry.state];
  return `In the views: ${shown.map((entry) => `${entry.name || "an assistant"} ${place(entry)}` +
    `${entry.near && (entry.state === "working" || entry.state === "finishing") ? ` near ${entry.near}` : ""}`).join("; ")}.`;
}

// The world's soundscape: its music, beds and one-shots, by their files'
// paths from the world's folder.
function soundscapeLine(soundscape) {
  const parts = [];
  if (soundscape.music) parts.push(`music ${soundscape.music}`);
  if (Array.isArray(soundscape.beds) && soundscape.beds.length) parts.push(`beds ${soundscape.beds.join(", ")}`);
  if (Array.isArray(soundscape.oneShots) && soundscape.oneShots.length) {
    parts.push(`one-shots ${soundscape.oneShots.map((set) => `(${(set || []).join(", ")})`).join(", ")}`);
  }
  return `Soundscape: the world's is ${soundscape.id}${parts.length ? `, playing ${parts.join("; ")}` :
    soundscape.music === undefined && !soundscape.beds && !soundscape.oneShots ? " (world_asset reads it)" : ", playing nothing"}`;
}

// What import of a lost file, or locate-file, did: the asset uses the file
// now, and so do any others that named the same lost file.
function repairedText(asset, message) {
  const others = typeof message.assetCount === "number" && message.assetCount > 1 ? message.assetCount - 1 : 0;
  return `The asset ${asset} (${message.kind || "?"}) uses ${message.source || "the file"} now${message.repaired ?
    ", in place of the file it had lost: no asset was added" : ""}${others ? `, and so ${others === 1 ? "does 1 other asset" :
    `do ${others} other assets`} that named the same lost file` : ""}. The objects that use it show it.`;
}

// An asset's lost file, as the state answer says it: missing, so the editor
// shows a placeholder in its place, or outside the project, which online
// keeps only its own files.
function lostFile(asset) {
  if (!asset.lost) return "";
  return `, its file ${asset.source || "?"} ${asset.lost === "missing" ? "is missing" : "is outside the project and will be lost"}`;
}

// One of a material's maps (material-map): "Stage 2 textures/lines.png,
// blend Alpha, keeping its alpha".
export function materialMap(map) {
  return `${map.map}${map.picture ? ` ${map.picture}` : ", no picture"}${map.blend ? `, blend ${map.blend}` : ""}` +
    `${map.picture ? map.keepsAlpha ? ", keeping its alpha" : ", without alpha" : ""}`;
}

// What material-map's answer says: the map as it is now, whether its picture
// kept its alpha and why, and a blend chosen for it.
function materialMapReport(message) {
  const parts = [message.pictureRemoved ? `${message.materialMap} of ${message.material} has no picture now.` :
    `${message.material}'s ${message.now ? materialMap(message.now) : message.materialMap}.`];
  if (message.alpha === "see-through") parts.push("The picture is see-through, so the map keeps its alpha (flag 2).");
  if (message.alpha === "opaque") parts.push("The picture is opaque, so the map takes no alpha from it.");
  if (message.alpha === "unknown") parts.push("This editor cannot tell whether the picture is see-through, so the map's " +
    "flags stay as they were.");
  if (message.blendChosen) parts.push("It is laid over the stages before it with blend Alpha, so they show where the " +
    "picture is clear.");
  return parts.join(" ");
}

// What an answer about a switch, door, timer or spawner says: its object and
// component, the helpers and assets made with it, its settings, connections
// and what Problems says, and what referred to a respawned object.
function gameplayReport(input, message) {
  const parts = [];
  if (input.name === "connect-behaviour") {
    parts.push(`The connection's id is ${message.connection}${message.updated ? " (changed, not added again)" : ""}.`);
    if (Array.isArray(message.responses)) parts.push(`Its responses are ${message.responses.join(" and ")}.`);
    if (message.doorActivation) parts.push(`The door now opens by ${message.doorActivation}.`);
  } else {
    parts.push(`The ${input.name === "make-spawner" ? "spawner" : input.behaviour} is component ${message.behaviour} of ` +
      `${message.object}.`);
    if (message.prefab) parts.push(`It spawns the prefab ${message.prefab}.`);
  }
  if (message.said) parts.push(`The editor says: ${message.said.replace(/[.!]?$/, ".")}`);
  if (Array.isArray(message.helpers) && message.helpers.length) parts.push(`Made with it: ${message.helpers.join(", ")}.`);
  if (Array.isArray(message.assets) && message.assets.length) parts.push(`New assets: ${message.assets.join(", ")}.`);
  if (message.settings && typeof message.settings === "object") parts.push(`Its settings: ${JSON.stringify(message.settings)}.`);
  if (Array.isArray(message.referrers) && message.referrers.length) {
    parts.push(`These referred to the object and now refer to its spawner: ${message.referrers.join(", ")}; ` +
      "check that each still makes sense.");
  }
  if (Array.isArray(message.problems) && message.problems.length) parts.push(`Problems: ${message.problems.join("; ")}.`);
  return parts.join(" ");
}

// A crowd's part of its object's line: "a crowd of 50 robot wandering a box
// 20 x 10 x 20 m, 50 people in the views, 48 walking", and with a limit on
// those shown at once, how many are.
function crowdText(crowd) {
  let text = `a crowd of ${crowd.crowd}`;
  if (typeof crowd.people === "number") {
    text += `, ${plural(crowd.people, "person", "people")} in the views${typeof crowd.walking === "number" ? `, ${crowd.walking} walking` : ""}`;
    if (typeof crowd.entities === "number" && typeof crowd.shown === "number") text += `, ${crowd.shown} shown`;
  }
  if (crowd.problem) text += ` (${CROWD_PROBLEMS[crowd.problem] || crowd.problem})`;
  return text;
}

// What a crowd's problem means, as CrowdSummary and the Problems list say.
const CROWD_PROBLEMS = {
  "no-models": "it has no characters to copy",
  "region-gone": "its terrain or painted region is gone, so it places nobody",
  "no-ground": "some of its people found no walkable ground in its area"
};

// The world's crowds, with any that place fewer people than asked or have
// a problem.
// A recipe's setting, as world_action recipe reads it: its id, kind and
// caption, its range and default.
function recipeSettingText(setting) {
  const range = Array.isArray(setting.range) ? `, ${number(setting.range[0])} to ${number(setting.range[1])}` +
    `${setting.unit ? ` ${setting.unit}` : ""}` : "";
  const items = Array.isArray(setting.items) ? `, one of ${setting.items.map((item) => JSON.stringify(item.value)).join(", ")}` : "";
  const given = setting.default !== undefined ? `, default ${JSON.stringify(setting.default)}` : "";
  return `${setting.id} (${setting.kind}, "${setting.caption}"${range}${items}${given}` +
    `${setting.section ? `, in ${setting.section}` : ""})`;
}

// Blip's recipes (world_action recipe, recipes.md): the recipes folder's,
// with any mistake, and the placed ones, with their settings and whether
// they are made with them.
export function recipesLine(recipes) {
  const available = Array.isArray(recipes.available) ? recipes.available : [];
  const placed = Array.isArray(recipes.placed) ? recipes.placed : [];
  if (!available.length && !placed.length && !recipes.editing) return "";
  const parts = [`Recipes: ${available.map((recipe) => {
    if (recipe.problem) return `${recipe.file} "${recipe.name}" (has a mistake, so no tile: ${recipe.problem})`;
    const notes = [];
    if (Array.isArray(recipe.settings) && recipe.settings.length) notes.push(`settings ${recipe.settings.join(", ")}`);
    if (recipe.autoMake) notes.push("made when placed");
    return `${recipe.file} "${recipe.name}"${notes.length ? ` (${notes.join("; ")})` : ""}`;
  }).join("; ") || "none saved"}`];
  if (placed.length) {
    parts.push(`placed: ${placed.map((entry) => `${entry.object}, ${entry.missing ? `of ${entry.file}, which is not in the ` +
      "recipes folder" : `a ${entry.name}${entry.settingsText ? ` with ${entry.settingsText}` : ""}`}, ` +
      (entry.making ? "you are making it" : entry.asked ? "Make asked you to make it" : !entry.everMade ? "not made yet" :
        `${plural(entry.made || 0, "object")} made${entry.changed ? ", settings changed since: make it again" : ""}`)).join("; ")}`);
  }
  // The recipe open in its tab, where the user may be changing it.
  if (recipes.editing && recipes.editing.file) {
    parts.push(`the user has ${recipes.editing.file} open in its tab${recipes.editing.unsaved ? ", with changes not saved yet: " +
      "ask them to save or close it before you change its file, or their Save is refused" : ""}`);
  }
  return `${parts.join("; ")}.`;
}

function crowdsLine(crowds) {
  return `Crowds: ${crowds.map((crowd) => `${crowd.id} "${crowd.name}" of ${crowd.count}` +
    (typeof crowd.entities === "number" ? `, the nearest ${crowd.entities} shown at once` : "") +
    (typeof crowd.people === "number" && crowd.people !== crowd.count ? `, ${crowd.people} placed` : "") +
    (crowd.problem ? ` (${CROWD_PROBLEMS[crowd.problem] || crowd.problem})` : "")).join("; ")}`;
}

// A painted region zone, as world_state and world_action say it: "region
// Swamp on hills, up to 30 m above the ground".
function regionZoneText(zone) {
  const height = typeof zone.height === "number" ? zone.height : 30;
  const reach = height < 0 ? `down to ${number(-height)} m below` : `up to ${number(height)} m above`;
  return `region ${zone.region || "?"} on ${zone.regionOf || zone.terrain || "?"}, ${reach} the ground` +
    (zone.regionMissing ? ` (its terrain or region is gone, so ${zone.crowd ? "it places nobody" : "nothing enters it"})` : "");
}

// The environment tab's preview scenes, by the names the editor gives them.
const PREVIEW_SCENES = { "stone-circle": "stone circle", forest: "forest", village: "village street", sphere: "sphere" };

// prepare-model's answer: the asset and file it saved (or, with check, would
// save), the triangles at full detail, and each level of detail's, with
// where it takes over.
function preparedReport(message) {
  const levels = Array.isArray(message.lods) ? message.lods : [];
  const made = message.checked ? (message.replaces ? `Preparing would replace ${message.prepared}'s file ${message.file}` :
    `Preparing would make the asset ${message.prepared}, in the new file ${message.file}`) : message.replaces ?
    `Prepared again: ${message.prepared}'s file ${message.file} is replaced` :
    `Prepared as the asset ${message.prepared}, in the new file ${message.file}${message.from ? `, made from ${message.from}` : ""}`;
  const original = typeof message.originalTriangles === "number" && message.originalTriangles !== message.triangles ?
    ` (the original has ${message.originalTriangles})` : "";
  const parts = [`${made}. Full detail: ${message.triangles} triangles${original}` +
    `${message.clips ? `, with ${plural(message.clips, "clip")}` : ""}.`];
  parts.push(levels.length ? `${plural(levels.length, "level")} of detail: ${levels.map((level) => `level ${level.level} ` +
    `${level.triangles} triangles from ${number(level.distance)} m`).join(", ")} (on a full HD screen, for the model at its ` +
    "own size)." : "No levels of detail: it is drawn at full detail at every distance.");
  if (message.levelsAsked) {
    parts.push(`${plural(message.levelsAsked, "level")} were asked for, but its shape would not simplify that far.`);
  }
  if (!message.checked && !message.replaces) {
    parts.push(message.moved ? `${plural(message.moved, "object")} that used ${message.from} now ` +
      `${message.moved === 1 ? "uses" : "use"} it.` : "No object uses it yet: world_action place places it.");
  }
  if (message.shown) parts.push("The user sees it in the Prepare tab.");
  if (message.note) parts.push(`${message.note}.`);
  return parts.join(" ");
}

// lod's answer: each model's simpler versions, as world_state lists them, or
// why the answer came before the views had made them.
function lodReport(message) {
  const objects = message.objects || [];
  if (!objects.length) return message.note ? `${message.note}.` : "";
  return objects.map((object) => `${object.id}: ${object.lod ? `level of detail ${object.lod}` :
    "full detail at every distance"}${typeof object.triangles === "number" ? ` (${object.triangles} triangles)` : ""}.`).join(" ");
}

// The world's baked lighting: baked or not, up to date or baking, its
// quality, what the views show, and the bake's warnings (too bright).
function lightingLine(lighting) {
  const parts = [`Baked lighting: ${lighting.status || (lighting.baked ? "baked" : "not baked")}`];
  if (typeof lighting.progress === "number") parts.push(`${lighting.progress}% done`);
  if (lighting.quality) parts.push(`quality ${lighting.quality}`);
  if (lighting.view && lighting.view !== "final") {
    parts.push(`the views show ${{ detail: "lightmap detail", probes: "the light probes", problems: "problem texels" }[lighting.view] ||
      "lighting only"}`);
  }
  return parts.join(", ") + (lighting.warnings ? `. Warning: ${lighting.warnings}` : "");
}

// Loading: whether distant objects load as the player gets near, what the
// world loads at the start and in all, and whether the views show what is
// loaded.
function loadingLine(loading) {
  const parts = [`Loading: ${loading.streaming ? "distant objects load as the player gets near" :
    "everything loads at the start"}`];
  if (loading.start && loading.total) {
    parts.push(loading.streaming ? `about ${loading.start} at the start, ${loading.total} in all` :
      `${loading.total} at the start${loading.start !== loading.total ? ` (${loading.start} if distant objects loaded later)` : ""}`);
  }
  if (loading.view) parts.push("the views show what is loaded");
  return parts.join(", ");
}

// How walking characters treat an object: its mode and what Automatic
// chose, its ground, an obstacle, a path they prefer, a zone's inside, and a
// walk link's settings or the link it ends.
function walkingText(walking) {
  const parts = [];
  if (walking.mode && walking.mode !== "automatic") {
    parts.push({ "walk-on": "characters walk on it, even while it moves", "walk-round": "characters walk round it",
      ignore: "left out of the walkable map" }[walking.mode] || `walking ${walking.mode}`);
  } else if (walking.automatic) {
    parts.push(`walkable map: ${walking.automatic}`);
  }
  if (walking.ground) parts.push(`its surfaces are ${walking.ground}`);
  if (walking.obstacle) parts.push("an obstacle characters walk round where it stops");
  if (walking.preferred !== undefined) parts.push(walking.preferred ? "a path characters prefer" : "a path characters don't prefer");
  if (walking.zoneGround) parts.push(walking.zoneGround === "normal" ? "characters walk through it as normal" :
    `inside it is ${walking.zoneGround}`);
  if (walking.linkTo) {
    parts.push(`a walk link${walking.linkKind ? ` (${walking.linkKind})` : ""} ${walking.bothWays === false ? "to" : "between it and"} ` +
      `${walking.linkTo}${typeof walking.linkCost === "number" && walking.linkCost !== 1 ? `, costing ${number(walking.linkCost)}` : ""}` +
      `${walking.open === false ? ", closed" : ""}`);
  }
  if (walking.linkEndOf) parts.push(`the far end of the walk link ${walking.linkEndOf}`);
  return parts.join(", ");
}

// Where characters can walk: each size, how much of the world it walks on
// (or the space a size that flies or swims has) and in how many parts, what
// is still being worked out, links and problems; the areas and their costs,
// and whether the views show it.
function navigationLine(navigation) {
  const sizes = (navigation.sizes || []).map((size) => {
    const moves = size.fly ? ", flies" : size.swim ? ", swims" : "";
    const name = `${size.name} (${number(size.radius)} x ${number(size.height)} m${moves})`;
    const open = typeof size.openCubicMetres === "number";
    if (!open && typeof size.walkableSquareMetres !== "number") return `${name}: ${size.map || "not worked out yet"}`;
    const parts = [open ? `${size.openCubicMetres} cubic metres it can ${size.swim ? "swim" : "fly"} through` :
      `${size.walkableSquareMetres} square metres walkable`];
    // A big world's map is worked out round the views and along routes, so
    // the area counts only that.
    if (size.big) parts[0] += ` worked out so far, within ${number(navigation.range ?? 250)} m of the views and along routes`;
    if (size.islands > 1) parts.push(`in ${size.islands} parts that don't join up`);
    if (size.tilesWaiting) parts.push(`${size.tilesWaiting} tiles still being worked out`);
    if (size.links) parts.push(plural(size.links, "walk link"));
    if (Array.isArray(size.problems) && size.problems.length) parts.push(`problems: ${size.problems.join("; ")}`);
    return `${name}: ${parts.join(", ")}`;
  });
  const areas = (navigation.areas || []).map((area) => area.closed ? `${area.name} closed` : `${area.name} ${number(area.cost)}`);
  return `Walking: ${sizes.join("; ") || "Person"}${areas.length ? `. Areas and their costs: ${areas.join(", ")}` : ""}` +
    `${navigation.updates === false ? ". The map changes only when the game calls UpdateNavigation" : ""}` +
    `${navigation.shown ? ". The views show the walkable ground" : ""}`;
}

export class WorldTools {
  // ide: { worldEditor() (the open editor's { path, run }, or null),
  // worldRequest(message, timeout) (runner.js request), openWorldEditor(path)
  // (whether it opened), confirmRemoval(removes, signal) (whether the user
  // said yes), projectPath(path), putToolFile(path) (gives the open editor a
  // file the project gained after it opened) }
  constructor(ide) {
    this.ide = ide;
    this.heard = null;        // the editor's run that has answered hello
    this.session = null;      // and its session (editor_requests.bb)
    this.deaf = null;         // { run, time } of one that did not
    this.begin();
  }

  // A new request of the user's: nothing done in the world yet.
  begin() {
    this.revision = null;     // the revision Blip last saw
    this.labels = [];         // his edits' labels, for the step's name
    this.pictures = 0;
    this.begun = false;       // whether the editor was told he began
  }

  open() { return !!this.ide.worldEditor(); }

  // Checks that this run of the editor answers, with a hello the first time.
  // Throws a WorldToolError when it is not open or does not answer.
  async reachable(timeout = HELLO_MS) {
    const editor = this.ide.worldEditor();
    if (!editor) throw new WorldToolError(NOT_OPEN);
    if (this.heard && this.heard === editor.run) return;
    if (this.deaf && this.deaf.run === editor.run && Date.now() - this.deaf.time < DEAF_MS) throw new WorldToolError(DEAF);
    let reply;
    try {
      reply = await this.ide.worldRequest({ protocol: PROTOCOL, version: 1, type: "hello" }, timeout);
    } catch (error) {
      if (error && (error.code === "ended" || error.code === "not-running")) throw new WorldToolError(NOT_OPEN);
      if (error && error.code !== "timeout") throw error;
      this.deaf = { run: editor.run, time: Date.now() };
      throw new WorldToolError(DEAF);
    }
    const message = reply.message && typeof reply.message === "object" ? reply.message : {};
    if (!message.ok) throw new WorldToolError(message.error || DEAF);
    this.heard = editor.run;
    this.session = message.session || null;
    this.deaf = null;
  }

  // Sends the editor a request and returns its answer, { message, file }.
  async request(type, members = {}, timeout = ANSWER_MS) {
    await this.reachable();
    let reply;
    try {
      reply = await this.ide.worldRequest({ protocol: PROTOCOL, version: 1, type, ...members }, timeout);
    } catch (error) {
      if (error && error.code === "timeout") {
        throw new WorldToolError("The World Editor did not answer in time. The user may have a dialog open, or be " +
          "dragging an object: try again in a moment.");
      }
      if (error && (error.code === "ended" || error.code === "not-running")) throw new WorldToolError(NOT_OPEN);
      throw error;
    }
    const message = reply.message && typeof reply.message === "object" ? reply.message : {};
    // The revision he has seen the world at, which his edits expect: begin's
    // answer is not a look, so what others changed before it still counts
    // as changed since he looked.
    if (message.world && message.world.revision !== undefined) this.revision = message.world.revision;
    if (message.revision !== undefined && type !== "begin") this.revision = message.revision;
    return { message, file: reply.file || null };
  }

  // Tells the editor Blip has begun a request, so that his edits become one
  // undo step. Once a request, when the editor is open.
  async beginRequest() {
    if (this.begun || !this.open()) return;
    try {
      await this.request("begin", {}, 5000);
      this.begun = true;
    } catch (error) {
      console.warn("The World Editor did not take Blip's begin:", error);
    }
  }

  // Tells the editor Blip has finished, which folds his edits into one step
  // and selects what he made. Returns { step, label, made, changed, removed }
  // when he changed the world, or null.
  async finishRequest() {
    if (!this.begun) return null;
    this.begun = false;
    const editor = this.ide.worldEditor();
    if (!editor) return null;
    const label = this.labels.join(", ").slice(0, 120);
    try {
      const { message } = await this.request("finish", label ? { label } : {}, 10000);
      if (!message.ok || !message.step) return null;
      // Besides objects: the assets he made or changed, such as a material or
      // a particle effect, the world's settings, and the terrains whose
      // ground he sculpted or painted, by name.
      const result = { step: message.step, session: message.session || null, label: message.label || label,
        made: message.made || [], changed: message.changed || [], removed: message.removed || [],
        assets: message.assets || [], settings: !!message.settings, ground: message.ground || [],
        world: editor.path || null };
      return result.made.length || result.changed.length || result.removed.length || result.assets.length ||
        result.settings || result.ground.length ? result : null;
    } catch (error) {
      console.warn("The World Editor did not take Blip's finish:", error);
      return null;
    }
  }

  // Tells the editor what Blip is doing, which its views show him doing
  // (editor_assistants.bb): thinking, working with a caption, idle, or
  // sleepy when the energy has run out, and his size for the effort level.
  // Nothing waits for it, and an editor that does not answer is left be.
  presence(members) {
    if (!this.open()) return;
    this.ide.worldRequest({ protocol: PROTOCOL, version: 1, type: "presence", ...members }, 3000).catch(() => {});
  }

  // The context's line about the editor, when it is open: its world,
  // revision, objects, selection, focus point and camera. Null when it does
  // not answer.
  async contextLine() {
    if (!this.open()) return null;
    try {
      const { message } = await this.request("state", { limit: 1 }, 5000);
      if (!message.ok) return null;
      const world = message.world || {};
      const camera = message.camera;
      // The desktop IDE's editor is a window of its own, and Play runs a
      // player linked to it.
      const where = this.ide.desktop ? "in a window of its own" : "in the program's place";
      const player = message.player;
      const playing = player ? ` The user is playing it in ${player.name || "a player"}${player.vr ? ", in VR" : ""}` +
        `${player.position ? `, at ${vector(player.position)}, facing yaw ${number(player.yaw)}` : ""}` +
        `${player.pointing ? `, pointing at ${player.pointing}` : ""}. The player shows each change at once; physics is ` +
        `${player.physics ? "running there now" : "paused while the world is edited"}. The play tool and world_look's player ` +
        "view work now." : "";
      if (this.ide.desktop) {
        return `The World Editor is open ${where} on ${world.path || "a new world"}${world.name ? ` ("${world.name}")` : ""}: ` +
          `revision ${world.revision}, ${plural(world.objects || 0, "object")}${world.unsaved ? ", unsaved changes" : ""}. ` +
          `Selected: ${(message.selection || []).join(", ") || "nothing"}. Focus point: ${vector(message.focus)}.` +
          `${camera ? ` Editor camera at ${vector(camera.position)}.` : ""} The world tools see and change it.${playing}`;
      }
      return `The World Editor is open, in the program's place, on ${world.path || "a new world"}` +
        `${world.name ? ` ("${world.name}")` : ""}: revision ${world.revision}, ${plural(world.objects || 0, "object")}` +
        `${world.unsaved ? ", unsaved changes" : ""}. Selected: ${(message.selection || []).join(", ") || "nothing"}. ` +
        `Focus point: ${vector(message.focus)}.${camera ? ` Editor camera at ${vector(camera.position)}.` : ""} ` +
        (world.readOnly ? `The world is read-only here (${world.readOnly}): the world tools see it, but cannot change it. ` :
          "The world tools see and change it. ") +
        "A run would close it, so run_program only compiles until the user closes it.";
    } catch {
      return null;
    }
  }

  describe(call) {
    const input = call.input || {};
    switch (call.name) {
      case "world_state": return "Looking at the world";
      case "world_entity": return `Looking at ${input.id || "an object"}`;
      case "world_asset": return `Looking at ${input.id || "an asset"}`;
      case "world_edit": return input.label ? `${input.label}` : "Changing the world";
      case "world_action": return input.label || ({
        add: input.kind === "particles" ? `Adding ${(input.look || "sparks").toLowerCase()}` : `Adding a ${input.kind || "object"}`,
        "add-model": `Adding ${input.asset || "a model"}`, place: `Placing ${input.asset || "an asset"}`,
        import: `Importing ${input.path || "a file"}`, "locate-file": `Finding the file of ${input.asset || "an asset"}`,
        "material-map": typeof input.picture === "string" && input.picture.toLowerCase() === "none" ?
          `Taking a picture off ${input.asset || "a material"}` : input.picture ? `Giving ${input.asset || "a material"} a picture` :
          `Blending ${input.asset || "a material"}`,
        paint: "Painting", physics: input.behavior === "none" ?
          "Taking physics away" : input.refit ? "Refitting a collider" : "Adding physics", joint: "Adding a joint",
        environment: input.object ? `Giving ${input.object} ${input.starter && input.starter.toLowerCase() !== "none" ?
          `the ${input.starter.toLowerCase()} look` : input.asset ? `the ${input.asset} look` : "the world's look"}` :
          input.starter && input.starter.toLowerCase() !== "none" ? `Making it ${input.starter.toLowerCase()}` :
          "Changing the sky", "environment-preview": "Showing the environment", "camera-effects": "Changing camera effects",
        sound: "Adding a sound", soundscape: input.remove ? "Taking a soundscape away" : input.music ?
          `Playing music${input.object ? ` in ${input.object}` : ""}` : `Changing the sounds${input.object ? ` of ${input.object}` : ""}`,
        lock: "Locking",
        unlock: "Unlocking", "physics-play": "Playing the physics", "physics-stop": "Stopping the physics",
        "set-online": input.on === false ? "Turning off online play" : "Making the world online",
        select: "Selecting", frame: "Framing", behaviour: input.code ? `Giving it ${input.code}` :
          `Adding ${String(input.behaviour || "a behaviour").replace(/-/g, " ")}`,
        "connect-behaviour": input.preset === "switch-door" ? `Connecting ${input.object || "a switch"} to ${input.target || "a door"}` :
          `Connecting ${input.object || "a behaviour"}`,
        "make-spawner": input.asset ? `Placing a spawner of ${input.asset}` : `Making ${input.object || "it"} respawn`,
        "test-behaviour": `Trying ${input.command || "a command"} in the preview`,
        "follow-ground": input.follow === "off" ? "Taking a path off the ground" : "Laying a path on the ground",
        "level-ground": input.level === false ? "Letting the hillside come back" : input.permanent ?
          "Making levelled ground permanent" : "Levelling the ground",
        sculpt: `${{ lower: "Lowering", smooth: "Smoothing", flatten: "Flattening", roughen: "Roughening", holes: "Cutting holes in",
          fill: "Filling holes in" }[input.tool] || "Raising"} the ground`,
        "paint-ground": `Painting ${input.layer || "the ground"}`, "auto-paint": "Painting the ground by its shape",
        "add-ground-layer": `Adding ${input.ground || (input.image ? input.image.replace(/^.*\//, "").replace(/\.[^.]*$/, "") : "a ground layer")} ground`,
        region: input.erase ? "Erasing regions" : `Painting ${input.region || "a region"}`,
        "take-photo": "Taking a photo", "cancel-photo": "Stopping the photo",
        "start-timelapse": "Starting a timelapse", "end-timelapse": "Ending the timelapse",
        "export-timelapse": "Exporting the timelapse video", "cancel-timelapse-export": "Cancelling video export",
        "keep-photo-camera": "Keeping the shot as a camera", "bake-lighting": "Baking the lighting", "cancel-lighting": "Cancelling the bake",
        "clear-lighting": "Clearing the baked lighting", "lighting-view": "Changing what the views show",
        "loading-view": "Changing what the views show",
        navigation: Object.keys(input).every((key) => ["name", "show"].includes(key)) ?
          (input.show === false ? "Hiding the walkable ground" : "Showing the walkable ground") :
          input.linkFrom ? "Adding a walk link" : "Changing how characters walk",
        lod: input.on === false ? "Drawing models at full detail" : "Making simpler versions", "lod-view":
          "Showing a simpler version",
        "prepare-model": input.check === true ? `Checking how ${input.asset || "a model"} would prepare` :
          `Preparing ${input.asset || "a model"}`,
        interior: input.program ? `Building ${String(input.program).replace(/^.*\//, "").replace(/\.bb$/i, "")}` :
          "Building the interior again", maplet: "Opening the interior in Maplet+",
        recipe: input.make ? `Making ${input.object || "a recipe"}` : input.done ? `Finishing ${input.object || "a recipe"}` :
          input.breakApart ? `Breaking ${input.object || "a recipe"} apart` : input.ids ? "Making objects a recipe" :
          input.position || input.positions ? `Placing ${String(input.path || "a recipe").replace(/^.*\//, "").replace(/\.recipe$/i, "")}` :
          input.settings ? "Changing a recipe's settings" : `Reading ${String(input.path || "a recipe").replace(/^.*\//, "")}`,
        "show-assistants": input.on === false ? "Hiding myself from the views" : "Showing myself in the views",
        account: input.account === "sign-in" ? "Signing you in to Blitz3D+" : input.account === "sign-out" ?
          "Signing this computer out of Blitz3D+" : "Seeing who is signed in",
        "blip-window": input.open === false ? "Closing my window" : "Opening my window",
        history: input.history === "redo" ? "Redoing the latest step" : "Undoing the latest step" })[input.name] ||
        "Using the World Editor";
      case "world_probe": return "Measuring";
      case "world_route": return "Finding a route";
      case "world_look": return input.view === "player" ? "Looking through your eyes" : input.view === "top" ?
        "Looking from above" : input.from ? "Looking from another place" : "Looking at the view";
      case "play": return ({ highlight: "Showing what I mean", move_user: "Taking you there", play_physics: "Running the physics",
        stop_physics: "Stopping the physics", say: input.text ? `Saying "${input.text}"` : "Talking" })[input.name] || "Using the player";
      case "open_world_editor": return input.path ? `Opening ${input.path} in the World Editor` : "Opening the World Editor";
      default: return "Working";
    }
  }

  async run(name, input, signal) {
    switch (name) {
      case "world_state": return this.state(input);
      case "world_entity": return this.entity(input);
      case "world_asset": return this.asset(input);
      case "world_edit": return this.edit(input, signal);
      case "world_action": return this.action(input);
      case "world_probe": return this.probe(input);
      case "world_route": return this.route(input);
      case "world_look": return this.look(input);
      case "open_world_editor": return this.openEditor(input);
      case "play": return this.play(input);
      default: throw new WorldToolError(`There is no tool called ${name}.`);
    }
  }

  async state(input) {
    const members = {};
    if (input.near !== undefined) {
      if (!isVector(input.near)) throw new WorldToolError("near is a point, [x, y, z].");
      members.near = input.near;
      if (input.radius !== undefined) {
        if (typeof input.radius !== "number" || !(input.radius >= 0)) throw new WorldToolError("radius is a distance, 0 or more.");
        members.radius = input.radius;
      }
    }
    if (input.ids !== undefined) {
      if (!Array.isArray(input.ids) || input.ids.some((id) => typeof id !== "string")) throw new WorldToolError("ids is a list of objects' ids.");
      members.ids = input.ids.slice(0, 500);
    }
    if (input.limit !== undefined) {
      if (!Number.isInteger(input.limit) || input.limit < 1 || input.limit > 500) throw new WorldToolError("limit is from 1 to 500.");
      members.limit = input.limit;
    }
    const { message, file } = await this.request("state", members);
    if (!message.ok) throw new WorldToolError(message.error || "The World Editor could not list the world.");
    const summary = worldSummary(message);
    if (message.photo?.state === "done" && file) {
      const picture = await this.ide.smallPicture(file.data, PICTURE_WIDTH);
      return [{ type: "text", text: summary }, { type: "image", source: {
        type: "base64", media_type: picture.type, data: picture.data } }];
    }
    return summary;
  }

  async entity(input) {
    if (typeof input.id !== "string" || !input.id) throw new WorldToolError("Give the object's id.");
    const { message } = await this.request("entity", { object: input.id });
    if (!message.ok) throw new WorldToolError(message.error || `There is no object ${input.id}.`);
    const parts = [objectLine(message.summary || { id: input.id, name: input.id })];
    parts.push(`As the world file keeps it (its rotation is a quaternion [x, y, z, w] there):\n` +
      JSON.stringify(message.entity, null, 1));
    // While the world plays in the editor: its switches', doors', timers' and
    // spawners' state there, which is the preview's, not a running game's.
    if (message.runtime && typeof message.runtime === "object") {
      parts.push(`Runtime, in the editor's preview at its step ${message.runtime.step} (not a running game's):\n` +
        JSON.stringify(message.runtime.behaviours || [], null, 1));
    }
    return parts.join("\n\n");
  }

  // One asset in full, with a resource kept in the world (inline) whole, and
  // what uses it.
  async asset(input) {
    if (typeof input.id !== "string" || !input.id) throw new WorldToolError("Give the asset's id.");
    const { message } = await this.request("asset", { asset: input.id });
    if (!message.ok) throw new WorldToolError(message.error || `There is no asset ${input.id}.`);
    const users = message.usedBy || [];
    const asset = message.asset || {};
    const parts = [`${input.id} is a ${asset.kind || "?"} asset${asset.inline ? ", kept in the world" :
      asset.source ? `, from the file ${asset.source}` : ""}. ${users.length ? `Used by ${users.join(", ")}.` :
      "No object uses it."}${message.environment ? " It is the world's environment." : ""}` +
      `${message.previewScene ? ` It is open in its tab, its preview on the ${PREVIEW_SCENES[message.previewScene] ||
        message.previewScene} scene.` : ""}` +
      `${typeof message.triangles === "number" ? ` It has ${message.triangles} triangles.` : ""}` +
      // A model Prepare Model saved: its recipe is options.prepared.
      `${message.preparedFrom ? ` Prepare Model made it from ${message.preparedFrom} (its recipe is options.prepared): ` +
        "world_action prepare-model changes it." : ""}` +
      `${typeof message.lodLevels === "number" ? ` Its file keeps ${plural(message.lodLevels, "level")} of detail, drawn far away.` :
        ""}` +
      `${message.problem ? ` It could not be loaded, so the editor shows a placeholder in its place: ${
        String(message.problem).replace(/([^.!?])$/, "$1.")}` : ""}` +
      `${message.lost ? ` Its file ${asset.source || "?"} ${message.lost === "missing" ? "is missing" :
        "is outside the project, which keeps only its own files, so it will be lost"}: ` +
        "import the file again, or give it another with world_action locate-file." : ""}` +
      `${Array.isArray(message.missingFiles) && message.missingFiles.length ? ` It names ${message.missingFiles.length === 1 ?
        "a file that is" : "files that are"} missing: ${message.missingFiles.join(", ")} (from the world's folder). The ` +
        "editor shows a placeholder for each, and the asset can still be changed: it keeps the paths, so it mends once " +
        "the files are back. Play waits for them." : ""}`];
    // A material's maps, read from its file when it has one of its own.
    if (Array.isArray(message.maps)) {
      parts.push(`It is a ${message.model || "?"} material. ${message.maps.length ? `Its maps: ` +
        `${message.maps.map(materialMap).join("; ")}.` : "It has no maps."}` +
        `${message.ownFile ? " It is kept in its own file, so material-map cannot change it: the user does, in its " +
          "Material tab." : message.model === "classic" || message.model === "shader" ?
          " world_action material-map gives a map a picture or a stage a blend." : ""}`);
    }
    if (message.layers?.length) {
      parts.push(`Overlay: ${message.layers.join(", ")}.`);
      if (message.ownFile && message.definition) asset.definition=message.definition;
    }
    // A physics asset's collider data, which the world file only names.
    if (Array.isArray(message.colliders) && message.colliders.length) {
      parts.push(`Its collider data: ${message.colliders.map((data) => `${data.alias || "?"}, ${colliderData(data)}`)
        .join("; ")}.`);
    }
    // A prefab as something to spawn: who spawns it, what it needs, and why
    // it cannot be spawned; a model is respawned once placed.
    if (Array.isArray(message.spawnedBy)) {
      parts.push(`${message.spawnedBy.length ? `Spawned by ${message.spawnedBy.join(", ")}.` : "No spawner makes it."} ` +
        `${message.spawnable === false ? `It cannot be spawned: ${message.notSpawnable}.` : "It can be spawned."}` +
        `${Array.isArray(message.inputs) && message.inputs.length ? ` Its inputs: ${message.inputs.map((input) =>
          `${input.id}${input.required === false ? " (optional)" : ""}`).join(", ")}.` : ""}` +
        `${message.spawnNeeds ? ` ${message.spawnNeeds.charAt(0).toUpperCase()}${message.spawnNeeds.slice(1)}.` : ""}`);
    } else if (message.spawn) {
      parts.push(`To respawn it: ${message.spawn}.`);
    }
    parts.push(`As the world file keeps it:\n${JSON.stringify(asset, null, 1)}`);
    if (message.photo?.live || message.photo?.timelapse?.available) parts.push(photoLine(message.photo));
    return parts.join("\n\n");
  }

  // What an edit or action did, for Blip.
  changes(message) {
    const parts = [];
    if ((message.made || []).length) parts.push(`made ${message.made.join(", ")}`);
    if ((message.changed || []).length) parts.push(`changed ${message.changed.join(", ")}`);
    if ((message.removed || []).length) parts.push(`removed ${message.removed.join(", ")}`);
    const extra = [];
    if (message.object) extra.push(`The new object is ${message.object}.`);
    if (message.textured) {
      const maps = (message.maps || []).filter((map) => map !== "baseColor");
      extra.push(`The texture${maps.length ? `, with its ${maps.map((map) => map === "metallicRoughness" ? "roughness" : map)
        .join(" and ")} map${maps.length > 1 ? "s" : ""},` : ""} repeats ${message.textured.map((made) =>
        `${number(made.across)} x ${number(made.down)} times on ${made.objects.join(", ")} (the material ${made.material})`).join("; ")}.`);
    } else if (message.material) {
      extra.push(`The material is the asset ${message.material}.`);
    }
    if (Array.isArray(message.layers)) for (const layer of message.layers)
      extra.push(`Overlay ${layer.picture} on ${layer.material}${layer.every ? `, every ${layer.every} m along` : ""}.`);
    if (message.untextured) extra.push("Its base textures are gone.");
    if (message.effects) extra.push(message.zone ? `The camera effects of cameras inside ${message.zone} are the asset ` +
      `${message.effects}.` : `The camera's effects are the asset ${message.effects}.`);
    // A zone's look: cameras going in see it blend in across the zone's fade.
    if (message.look !== undefined) extra.push(message.look ? `Cameras inside ${message.zone} show the environment ` +
      `${message.look}, blended in across its fade.` : `Cameras inside ${message.zone} show the world's own look.`);
    if (message.sound) extra.push(`The sound is the asset ${message.sound}.`);
    if (message.environment !== undefined) extra.push(message.environment ? `The world's environment is the asset ` +
      `${message.environment}.` : "The world has no environment now: its own ambient light applies.");
    // The ground: a path on it, an object levelling it, sculpting and painting.
    if (typeof message.ground === "string") extra.push(`The path follows the ground of ${message.terrain || "its terrain"} ` +
      `(follow ${message.ground}); each point's y is now its height above the ground.`);
    if (message.levels && !message.permanent) extra.push(`It levels the ground of ${message.levels}.`);
    if (message.layersAdded) extra.push(`${message.layersAdded}.`);
    if (typeof message.rose === "number") extra.push(`The ground of ${message.terrain} rose by up to ${number(message.rose)} ` +
      `and fell by up to ${number(message.fell)}.`);
    if (message.layer) extra.push(`Painted ${message.layer} on ${message.terrain}.`);
    if (message.painted) extra.push(`Painted ${message.terrain} by its shape: ${message.painted}.`);
    if (message.groundLayer) extra.push(message.groundType ? `${message.terrain || "The terrain"} has a new ground type, ` +
      `${message.groundLayer} (number ${message.layerNumber} of up to 256): paint-ground paints it by that name, and up to ` +
      "eight types can meet in one place." : `${message.terrain || "The terrain"} has a new ground layer, ${message.groundLayer} ` +
      `(layer ${message.layerNumber} of up to 8): paint-ground paints it by that name.`);
    if (message.typeChanged) extra.push(`The ground type ${message.typeChanged} of ${message.terrain || "the terrain"} was ` +
      `${message.typeChanges}.`);
    if (typeof message.across === "number" && message.tiles) extra.push(`${message.terrain || "The terrain"} is now ` +
      `${number(message.across)} wide, in ${message.tiles} x ${message.tiles} tiles; its ground stayed where it was.`);
    if (message.map) extra.push(message.region ? `The region ${message.region} is on ${message.map}, a region map of ` +
      `${message.terrain || "the terrain"}.` : `The ${message.map} region map's regions were erased there.`);
    if (message.permanent) extra.push(`The levelled ground is now ${message.terrain}'s own, and the object no longer shapes it.`);
    if (message.collider && typeof message.collider === "object") extra.push(`Its ${colliderText(message.collider)}.`);
    if (message.remark) extra.push(`The editor says: ${message.remark.replace(/[.!]?$/, ".")}`);
    if (message.note) extra.push(`${message.note}.`);
    // What others, the user or another assistant, changed since he last
    // looked, which did not stop his edit: worth a look before he relies on it.
    if (Array.isArray(message.meanwhile) && message.meanwhile.length) {
      extra.push(`Meanwhile ${message.meanwhile.slice(0, 6).map((other) => `${other.by || "someone"} ` +
        `${(other.changed || []).length ? `changed ${other.changed.slice(0, 12).join(", ")}` : "changed the world"}` +
        `${other.label ? ` (${other.label})` : ""}`).join("; ")}.`);
    }
    if (message.revision === undefined) return [parts.length ? `Done: ${parts.join("; ")}.` : "Done.", ...extra].join(" ");
    return [parts.length ? `Done: ${parts.join("; ")}.` : "Done.", ...extra, `The world is at revision ${message.revision}.`]
      .join(" ");
  }

  async edit(input, signal) {
    if (typeof input.label !== "string" || !input.label.trim()) throw new WorldToolError("Give the edit a label, such as Add 8 pillars.");
    if (!Array.isArray(input.operations) || !input.operations.length || input.operations.length > MAX_OPERATIONS ||
        input.operations.some((operation) => !operation || typeof operation !== "object" || Array.isArray(operation))) {
      throw new WorldToolError(`operations is a list of 1 to ${MAX_OPERATIONS} operation objects.`);
    }
    await this.beginRequest();
    const label = input.label.trim().slice(0, 80);
    // Only the user's click confirms a large removal: a mark of Blip's own
    // in the input is not passed on.
    const members = { label, operations: input.operations };
    if (this.revision !== null) members.expectedRevision = this.revision;
    await this.giveSources(input.operations);
    let { message } = await this.request("edit", members);
    if (!message.ok && message.needsConfirmation) {
      const yes = this.ide.confirmRemoval ? await this.ide.confirmRemoval(message.removes || [], signal) : false;
      if (!yes) throw new WorldToolError("The user said no: they want to keep those objects. Do not remove them.");
      ({ message } = await this.request("edit", { ...members, confirmed: true }));
    }
    if (!message.ok) {
      // Refused only when someone else, the user or another assistant,
      // changed what it touches since he last looked: the editor says who
      // and what.
      if (message.conflict) {
        throw new WorldToolError(`${message.error || "The world changed since you last looked."} world_state shows ` +
          "it as it is now.");
      }
      const where = message.operation !== undefined ? ` (operation ${message.operation}${message.path ? `, ${message.path}` : ""})` : "";
      throw new WorldToolError(`The World Editor refused the edit${where}: ${message.error}`);
    }
    this.labels.push(label);
    return this.changes(message);
  }

  async action(input) {
    // add-model is place's old name.
    const names = ["add", "place", "add-model", "import", "locate-file", "paint", "material-map", "physics", "joint", "environment",
      "environment-preview", "camera-effects", "sound", "soundscape", "behaviour", "follow-ground", "level-ground", "sculpt",
      "paint-ground", "auto-paint",
      "add-ground-layer", "region", "lock", "unlock", "physics-play", "physics-stop", "set-online", "navigation", "select", "frame",
      "bake-lighting", "take-photo", "cancel-photo", "keep-photo-camera",
      "start-timelapse", "end-timelapse", "export-timelapse", "cancel-timelapse-export",
      "cancel-lighting", "clear-lighting", "lighting-view", "loading-view", "lod", "lod-view", "terrain-ground-type", "terrain-grow",
      "interior", "maplet", "zone-shape", "ground-speed", "crowd",
      // Blip's recipes: his saved prompts, placed and made (recipes.md).
      "recipe",
      // A model made ready before a game loads it (Prepare Model).
      "prepare-model",
      // Switches, doors, timers and spawners: their connections, respawning
      // and a command tried in the editor's preview.
      "connect-behaviour", "make-spawner", "test-behaviour",
      "show-assistants",
      // The computer's Blitz3D+ account (editor_account.bb).
      "account",
      // View > Blip Helper, and Edit > Undo and Redo (blip_host.bb).
      "blip-window", "history"];
    if (!names.includes(input.name)) throw new WorldToolError(`name is one of ${names.filter((name) => name !== "add-model")
      .join(", ")}.`);
    for (const key of ["position", "point", "axis", "size", "linkFrom", "linkTo"]) {
      if (key === "size" && ["take-photo", "start-timelapse"].includes(input.name) &&
          ["hd", "full-hd", "4k", "square", "portrait"].includes(input.size)) continue;
      // A new terrain's size is a name or its metres across.
      if (key === "size" && input.kind === "terrain" && (["small", "medium", "large", "huge"].includes(input.size) ||
          (typeof input.size === "number" && input.size > 0))) continue;
      if (input[key] !== undefined && !isVector(input[key])) {
        throw new WorldToolError(key === "size" && input.kind === "terrain" ? "size is small, medium, large or huge, or " +
          "the metres across." : `${key} is [x, y, z].`);
      }
    }
    if (["take-photo", "start-timelapse"].includes(input.name)) {
      for (const key of ["from", "target"]) if (input[key] !== undefined && !isVector(input[key])) {
        throw new WorldToolError(`${key} is a point, [x, y, z].`);
      }
      if (input.focus !== undefined && typeof input.focus !== "string" && !isVector(input.focus)) {
        throw new WorldToolError("focus is an object's id or a point, [x, y, z].");
      }
      if ((input.width === undefined) !== (input.height === undefined)) throw new WorldToolError("Give both width and height.");
      for (const key of ["width", "height"]) if (input[key] !== undefined &&
          (!Number.isInteger(input[key]) || input[key] < 1 || input[key] > 8192)) {
        throw new WorldToolError(`${key} is a whole number from 1 to 8192.`);
      }
      if (input.quality !== undefined && !["draft", "good", "best", "path-traced"].includes(input.quality)) {
        throw new WorldToolError("Photo quality is draft, good, best or path-traced.");
      }
      if (input.blur !== undefined && (!Number.isFinite(input.blur) || input.blur < 0 || input.blur > 1)) {
        throw new WorldToolError("blur is from 0 to 1.");
      }
    }
    if (input.name === "start-timelapse" && input.folder !== undefined && typeof input.folder !== "string") throw new WorldToolError("folder is a new local directory.");
    if (input.name === "export-timelapse" && input.fps !== undefined && (!Number.isInteger(input.fps) || input.fps < 1 || input.fps > 120)) throw new WorldToolError("fps is a whole number from 1 to 120.");
    if (input.tint !== undefined && !isVector(input.tint)) throw new WorldToolError("tint is [r, g, b], each from 0 to 255.");
    if (input.color !== undefined && !isVector(input.color)) throw new WorldToolError("color is [r, g, b], each from 0 to 255.");
    for (const key of ["ids", "objects", "effects"]) {
      if (input[key] !== undefined && (!Array.isArray(input[key]) || input[key].some((id) => typeof id !== "string"))) {
        throw new WorldToolError(`${key} is a list of ${key === "effects" ? "effects' names" : "objects' ids"}.`);
      }
    }
    if (input.settings !== undefined && (!input.settings || typeof input.settings !== "object" || Array.isArray(input.settings))) {
      throw new WorldToolError(input.name === "recipe" ? 'settings is an object of the recipe\'s settings by id, such as { "size": 12 }.' :
        'settings is an object of the behaviour\'s settings, such as { "speed": 180 }.');
    }
    // A recipe placed at several points at once.
    if (input.positions !== undefined && (!Array.isArray(input.positions) || !input.positions.length ||
        input.positions.length > 256 || !input.positions.every(isVector))) {
      throw new WorldToolError("positions is a list of 1 to 256 points in the world, each [x, y, z].");
    }
    // A stroke on a terrain: its points in the world.
    if (input.points !== undefined && (!Array.isArray(input.points) || !input.points.length || input.points.length > 256 ||
        !input.points.every(isVector))) {
      throw new WorldToolError("points is a list of 1 to 256 points in the world, each [x, y, z].");
    }
    const members = { name: input.name };
    if (input.name === "start-timelapse" && input.folder !== undefined) members.folder = input.folder;
    if (input.name === "export-timelapse" && input.fps !== undefined) members.fps = input.fps;
    if (input.name === "account" && input.account !== undefined) members.account = input.account;
    if (input.name === "history" && input.history !== undefined) members.history = input.history;
    // pattern is a light cookie's (add, kind light-cookie).
    for (const key of ["kind", "look", "pattern", "asset", "position", "parent", "objectName", "path", "object", "objects", "other",
      "behavior", "refit", "ids", "label", "color", "roughness", "metallic", "glow", "opacity", "materialName", "starter",
      "effects", "loop", "volume", "remove", "joint", "point", "axis", "length", "behaviour", "code", "settings", "size",
      // The ground's (follow-ground, level-ground, sculpt, paint-ground, auto-paint, add-ground-layer and region).
      "follow", "terrain", "drape", "sink", "steepest", "bankSlope", "widestBank", "level", "shaping", "footprint", "bury",
      "order", "permanent", "tool", "points", "radius", "softness", "by", "height", "strength", "layer", "map", "region",
      "erase", "steep", "high", "low", "elsewhere", "steepAbove", "highAbove", "lowBelow", "blend", "ground", "image",
      // A region filled inside an outline (region), and a zone's shape (zone-shape).
      "fill", "shape",
      // A terrain's ground types (terrain-ground-type).
      "groundType", "typeName", "tint", "tiling",
      // Baked lighting's (bake-lighting and lighting-view), and level of detail's (lod and lod-view).
      "quality", "view", "on", "versions", "fade", "cullRange",
      // The environment tab's preview (environment-preview), and paint's texture.
      "scene", "texture", "tileSize", "layers",
      // An interior's program (interior).
      "program",
      // A recipe's places, and a run of his on a placed one (recipe).
      "positions", "make", "done", "breakApart",
      // A model's preparation (prepare-model): its steps and levels of detail.
      "steps", "lods", "animated", "use", "assetName", "check",
      // Played together online (set-online).
      "player", "moves", "players", "bots", "viewDistance", "idleSeconds",
      // Walking characters (navigation).
      "mode", "walking", "obstacle", "character", "removeCharacter", "area", "cost", "closed", "updates", "range", "linkFrom", "linkTo",
      "linkKind", "bothWays", "linkCost", "open", "show", "walks", "clip",
      // A crowd's people, and what they do (crowd).
      "count", "density", "model", "models", "activity", "paths", "places", "target", "variety", "speeds", "avoidance",
      "detail", "entities", "tag", "seed", "shared",
      // A soundscape's music and its fades, beds and one-shot sets, added to
      // what it has (soundscape).
      "music", "musicVolume", "fadeIn", "fadeOut", "beds", "oneShots", "add",
      // A switch's, door's, timer's or spawner's connection, and a command to
      // try (connect-behaviour, test-behaviour).
      "component", "preset", "targetBehaviour", "connection", "event", "do", "command",
      // A material's map and its picture (material-map).
      "stage", "picture", "from", "width", "focus", "blur", "file", "keep"]) {
      if (input[key] !== undefined) members[key] = input[key];
    }
    // paint takes a list of objects, or one.
    if (input.name === "paint" && members.objects === undefined && typeof input.object === "string") members.objects = [input.object];
    // A sound file goes to the editor first, named by its path from the world's folder.
    if (input.name === "sound" && typeof input.path === "string" && input.path) {
      members.source = await this.soundSource(input.path);
      delete members.path;
    }
    // So do a soundscape's: its music, its beds' and its one-shots'.
    if (input.name === "soundscape") await this.soundscapeSources(input, members);
    // navigation with only show changes the views, not the world.
    const viewOnly = input.name === "navigation" && Object.keys(input).every((key) => ["name", "show", "label"].includes(key));
    // recipe with a path alone only reads it.
    const readsRecipe = input.name === "recipe" && typeof input.path === "string" && input.path && input.position === undefined &&
      input.positions === undefined && input.ids === undefined;
    // prepare-model with check only measures.
    const changes = !viewOnly && !readsRecipe && !(input.name === "prepare-model" && input.check === true) && !["select", "frame", "lock",
      "unlock", "physics-play", "physics-stop", "cancel-lighting",
      "lighting-view", "loading-view", "environment-preview", "lod-view", "ground-speed", "test-behaviour",
      "show-assistants", "take-photo", "cancel-photo", "start-timelapse", "end-timelapse", "export-timelapse", "cancel-timelapse-export", "account",
      // Edit > Undo is a step of the history, not of his request.
      "blip-window", "history"].includes(input.name);
    if (changes) await this.beginRequest();
    if (input.name === "interior") return await this.interior(input, members);
    if (input.name === "maplet") return await this.maplet(input, members);
    if (input.name === "recipe") return await this.recipe(input, members, readsRecipe);
    // A model, picture or sound is added with a path that works everywhere.
    const file = input.name === "import" && typeof input.path === "string" && this.ide.projectPath ?
      this.ide.projectPath(input.path.replace(/\\/g, "/")) : null;
    if (file && ASSET_KINDS[file.replace(/^.*\./, "").toLowerCase()]) {
      const label = input.label || `Import ${file.replace(/^.*\//, "")}`;
      const { asset, kind, message } = await this.importFile(file, label);
      return `${message.repaired ? repairedText(asset, message) : `Imported as the asset ${asset} (${kind}).`} ` +
        this.changes(message);
    }
    // The editor has the project's files as they were when it opened: one
    // added since, such as a model Blip had made, is given to it first.
    if (input.name === "import" || input.name === "locate-file") await this.giveResource(input.path);
    if ((input.name === "add-ground-layer" || input.name === "terrain-ground-type") && typeof input.image === "string" &&
        input.image) await this.giveFile(input.image);
    // paint's texture, with the maps make_picture saved beside it.
    if (input.name === "paint" && typeof input.texture === "string" && input.texture && input.texture.toLowerCase() !== "none") {
      const color = input.texture.replace(/\\/g, "/");
      await this.giveFile(color);
      for (const suffix of Object.values(MAP_SUFFIXES)) {
        const map = color.replace(/(\.[a-z0-9]{1,5})?$/i, `${suffix}.png`);
        let found = null;
        try {
          found = this.ide.projectPath ? this.ide.projectPath(map) : null;
        } catch {}
        if (found) await this.giveFile(found);
      }
    }
    if (input.name === "paint" && Array.isArray(input.layers)) {
      for (const layer of input.layers) for (const role of ["texture", "normal", "metallicRoughness"]) {
        const file = layer?.[role];
        if (typeof file === "string" && file && !file.startsWith("markings/")) await this.giveFile(file.replace(/\\/g, "/"));
      }
    }
    // material-map's picture, which may be new since the editor opened.
    if (input.name === "material-map" && typeof input.picture === "string" && input.picture &&
        input.picture.toLowerCase() !== "none") await this.giveFile(input.picture.replace(/\\/g, "/"));
    // A behaviour of the world's own code: the editor reads its code, from
    // behaviours/ beside the world, as the code editor has it now
    // (docs/WEB_CODE_BEHAVIOURS_DESIGN.md).
    if (input.name === "behaviour" && typeof input.code === "string" && input.code) {
      const editor = this.ide.worldEditor();
      const folder = editor && editor.path ? editor.path.replace(/[^/]*$/, "") : "";
      const code = this.ide.projectPath ? this.ide.projectPath(`${folder}behaviours/${input.code}.bb`) : null;
      if (code && this.ide.putToolFile) {
        const open = this.ide.editor && this.ide.editor.textOf ? this.ide.editor.textOf(code) : null;
        await this.ide.putToolFile(code, typeof open === "string" ? new TextEncoder().encode(open) : null);
      }
    }
    // A level of detail's model file, which may be new since the editor
    // opened: its path is from the world's folder.
    if (input.name === "prepare-model" && Array.isArray(input.lods)) {
      const editor = this.ide.worldEditor();
      const folder = editor && editor.path ? editor.path.replace(/[^/]*$/, "") : "";
      for (const level of input.lods) {
        if (level && typeof level.file === "string" && level.file) await this.giveFile(`${folder}${level.file}`);
      }
    }
    const { message } = await this.request("action", members);
    if (!message.ok) throw new WorldToolError(message.error || "The World Editor could not do that.");
    if (input.name === "account") return accountLine(message.account, message.message);
    if (input.name === "blip-window") return message.open ? message.window ? "The IDE's Blip window is open, which serves this World Editor." :
      "Your window is open beside the World Editor (View > Blip Helper)." : "Your window in the World Editor is closed.";
    if (input.name === "history") return `${input.history === "redo" ? "Edit > Redo put back" : "Edit > Undo took back"} "${message.step}".` +
      `${message.said ? ` ${message.said}.` : ""} world_state shows the world as it is now.`;
    if (input.name === "take-photo") return `Photo started; saves to ${message.file}. Use world_state to follow it and see the finished picture.`;
    if (input.name === "cancel-photo") return photoLine(message.photo);
    if (["start-timelapse", "end-timelapse", "export-timelapse", "cancel-timelapse-export"].includes(input.name)) return photoLine(message.photo);
    // prepare-model: what the prepared model has; with check, only that.
    if (input.name === "prepare-model") {
      if (message.checked) return `${preparedReport(message)} Only checked: nothing was saved, and the world is unchanged.`;
      this.labels.push(input.label || this.describe({ name: "world_action", input }));
      return `${preparedReport(message)} ${this.changes(message)}`;
    }
    if (viewOnly) {
      return `${message.said ? `${message.said.replace(/[.!]?$/, ".")} ` : ""}Only the views change, not the world.`;
    }
    if (!changes) {
      // test-behaviour only changes the editor's preview.
      if (input.name === "test-behaviour") {
        return `${message.said ? `${message.said.replace(/[.!]?$/, ".")} ` : ""}Command ${message.sequence} of this preview, ` +
          `queued at its step ${message.step}; now: ${message.state || "unknown"} Only the editor's preview changes: ` +
          "physics-stop puts everything back, and the world and any running game are unchanged.";
      }
      // ground-speed only measures: a clip's ground speed, from its root motion.
      if (input.name === "ground-speed") {
        return `${message.said.replace(/[.!]?$/, ".")} To use it, set the ${message.clip} clip's groundSpeed to ` +
          `${number(message.groundSpeed)} in ${message.set}'s definition.clips. Only measured: the world is unchanged.`;
      }
      return ({ select: "Selected.", frame: "Framed in the user's view.", lock: `Locked ${plural((input.ids || []).length, "object")}: ` +
        "the user cannot pick or change them, and nor can you, until they are unlocked.", unlock: `Unlocked ` +
        `${plural((input.ids || []).length, "object")}.`, "physics-play": "Physics is playing in the editor, from the " +
        "world as authored. The world cannot be changed while it plays: stop it (physics-stop) first.", "physics-stop":
        "Physics has stopped, and everything is back where it was.", "cancel-lighting": message.note ? `${message.note}.` :
        "The bake is cancelled: the world keeps the lighting it had.", "lighting-view": "The views show " +
        `${{ final: "the finished look", lighting: "the lighting alone, on white surfaces", detail: "the lightmap texels " +
          "as a checker", probes: "the light probes, as crosses in the colour of their light", problems: "in red the " +
          "lightmap texels inside other objects, which take their neighbours' light and can let it leak" }[message.view] ||
          "the finished look"}. Only the views change, not the world.`, "loading-view": message.view === "loaded" ?
          "The views show what a game camera where they look from would load: loaded objects as they are, the rest " +
          "as outlines. Move the view to see the distances work. Only the views change, not the world." :
          "The views show the whole world again.", "lod-view": message.view === "auto" ?
          "The views choose each model's version by its distance again." : `The views show ${input.object} ` +
          `${message.view === "full" ? "at full detail" : `as its simpler version ${message.view}`}` +
          `${typeof message.triangles === "number" ? ` (${message.triangles} triangles)` : ""} at any distance, while it ` +
          "stays selected. Only the views change, not the world.", "environment-preview": `The user sees the ` +
          `${message.environment} environment in its tab, its preview on the ${PREVIEW_SCENES[message.scene] || message.scene} ` +
          "scene. Only the tab changes, not the world.", "show-assistants": message.shown ? "The views show you and " +
          "other AI assistants at work again." : "The views no longer show you or other AI assistants (View > Blip and AI " +
          "Assistants). Only the views change, not the world." })[input.name];
    }
    // A bake runs on after the answer: world_state's lighting follows it.
    if (input.name === "bake-lighting") {
      if (message.revision !== undefined) this.labels.push(input.label || this.describe({ name: "world_action", input }));
      const quality = message.lighting && message.lighting.quality ? ` at ${message.lighting.quality} quality` : "";
      return `${message.note ? `${message.note}. ` : ""}Baking${quality}: the views keep working and fill in as it bakes. ` +
        "When it ends, the editor keeps the bake beside the world and names it in the world's settings, as a step of its " +
        "own (Edit > Undo takes it back). world_state's Baked lighting line says how far it has got and when it is done, and warns when the bake is too bright.";
    }
    // Nothing needed changing, such as a behaviour it has already.
    if (message.note && message.revision === undefined) {
      return `${message.note}: nothing changed.${input.name === "lod" ? ` ${lodReport(message)}` : ""}`;
    }
    this.labels.push(input.label || this.describe({ name: "world_action", input }));
    let text = this.changes(message);
    // A painted region zone: the region it covers, and how high.
    if ((input.name === "add" || input.name === "zone-shape") && message.region) {
      text = `The zone ${message.object} covers ${regionZoneText(message)}. ${text}`;
    }
    // lod: each model's versions, once the views have made them.
    if (input.name === "lod") text = `${text} ${lodReport(message)}`;
    if (message.asset) {
      text = `${input.name === "locate-file" || message.repaired ? repairedText(message.asset, message) :
        `Imported as the asset ${message.asset} (${message.kind}).`} ${text}`;
    }
    if (input.name === "set-online") {
      const online = message.online || {};
      const parts = [message.said ? `The editor says: ${message.said.replace(/[.!]?$/, ".")}` : "Done."];
      parts.push(`settings.online is now ${JSON.stringify(online)}.`);
      if (message.note) parts.push(`${message.note}.`);
      if (online.enabled !== false) parts.push("The user tries it with Play > Play Online Test.");
      text = `${parts.join(" ")} ${text}`;
    }
    if (input.name === "navigation") {
      const parts = [message.said ? `The editor says: ${message.said.replace(/[.!]?$/, ".")}` : "Done."];
      if (message.link) parts.push(`The walk link is the object ${message.link}, its far end under it.`);
      if (message.navigation) parts.push(`settings.navigation is now ${JSON.stringify(message.navigation)}.`);
      parts.push("Check with world_route once it is built.");
      text = `${parts.join(" ")} ${text}`;
    }
    // A crowd: what it now is, and the region it keeps to.
    if (input.name === "crowd") {
      const parts = [message.crowd ? `The crowd ${message.object} is ${message.crowd}.` : `The crowd is ${message.object}.`];
      if (message.region) parts.push(`Its people keep to region ${message.region} of ${message.terrain}.`);
      if (message.note) parts.push(`The editor says: ${message.note.replace(/[.!]?$/, ".")}`);
      parts.push("Its people walk while physics plays and in Play; world_entity says how many it placed.");
      text = `${parts.join(" ")} ${text}`;
    }
    // A material's map as it is now, and what its picture's alpha did.
    if (input.name === "material-map") return `${materialMapReport(message)} ${text}`;
    // A switch, door, timer or spawner, and what was made with it; a
    // connection; a respawning object or a spawn point.
    if (["connect-behaviour", "make-spawner"].includes(input.name) ||
        (input.name === "behaviour" && ["switch", "door", "timer", "spawner"].includes(input.behaviour))) {
      return `${gameplayReport(input, message)} ${text}`;
    }
    if (input.name === "behaviour") {
      const parts = [message.zone ? `The trigger zone is the object ${message.zone}.` : `The behaviour's id is ${message.behaviour}.`];
      if (message.said) parts.push(`The editor says: ${message.said.replace(/[.!]?$/, ".")}`);
      if (message.job) parts.push(`It ${message.job}; world_entity says what it walks as.`);
      if (message.untagged) {
        parts.push(`Nothing is tagged ${message.untagged} yet, so nothing can ${message.zone ? "enter it" : "collect it"}: ` +
          `tag the player's object (the camera, for the World Editor's Play) with entity.metadata, adding ${message.untagged} ` +
          "to its tags.");
      }
      text = `${parts.join(" ")} ${text}`;
    }
    return text;
  }

  // world_action recipe (recipes.md, blip_recipes_requests.bb): a recipe of
  // the world's recipes folder read, placed or made from objects, and runs
  // of his on placed ones. The recipe, its icon and its kept files may be new
  // since the editor opened, so they are given to it first.
  async recipe(input, members, reads) {
    if (typeof input.path === "string" && input.path) await this.giveRecipe(input.path);
    const { message } = await this.request("action", members, ANSWER_MS);
    if (!message.ok) throw new WorldToolError(message.error || "The World Editor could not do that.");
    if (reads) {
      const settings = Array.isArray(message.settings) ? message.settings : [];
      const parts = [`${message.said || `Read ${message.recipe}.`}`];
      if (message.help) parts.push(`Its help: ${message.help}`);
      parts.push(`Its prompt: ${message.prompt}`);
      parts.push(settings.length ? `Its settings: ${settings.map(recipeSettingText).join("; ")}.` : "It has no settings.");
      if (Array.isArray(message.kept) && message.kept.length) parts.push(`Its kept files: ${message.kept.join(", ")}.`);
      parts.push(message.asks === false ? "It is made when placed: placing it from its tile asks you to make it at once, with " +
        "its settings as they start." : "Placed from its tile, it waits for the user to choose its settings and press Make.");
      return `${parts.join(" ")} Nothing in the world changed.`;
    }
    // His step's name: the recipe's, as the editor names a step that made one.
    const placing = input.position || input.positions;
    this.labels.push(input.label || (message.name ? (placing ? `Place ${message.name}` : input.ids ? `Make ${message.name} a recipe` :
      message.name) : this.describe({ name: "world_action", input })));
    const parts = [];
    if (message.said) parts.push(message.said.replace(/[.!]?$/, "."));
    if (input.make) {
      parts.push(`Its prompt, with its settings filled in: ${message.prompt}`);
      if (message.settingsText) parts.push(`Its settings: ${message.settingsText}.`);
      if (typeof message.takenBack === "number") parts.push(`Its last run's ${plural(message.takenBack, "object")} ` +
        "went first, in this step, so Undo brings them back.");
      if (Array.isArray(message.kept) && message.kept.length) parts.push(`The recipe keeps ${message.kept.join(", ")}: ` +
        "use them rather than making them again.");
      parts.push("Make it now, round its recipe object's position: what you add at the world's root goes under the object " +
        "when you say done (done true), or when your answer ends.");
    }
    if (Array.isArray(message.objects) && message.objects.length && placing) {
      parts.push(`The recipe ${message.objects.length === 1 ? "object is" : "objects are"} ${message.objects.join(", ")}.`);
    }
    return `${parts.join(" ")} ${this.changes(message)}`;
  }

  // A recipe of the world's recipes folder given to the editor, with its
  // icon and kept files, from its own folder.
  async giveRecipe(path) {
    const editor = this.ide.worldEditor();
    const folder = editor && editor.path ? editor.path.replace(/[^/]*$/, "") : "";
    const plain = path.replace(/\\/g, "/");
    for (const candidate of [plain, `recipes/${plain}`, `recipes/${plain.replace(/^.*\//, "")}`]) {
      let file = null;
      try {
        file = this.ide.projectPath ? this.ide.projectPath(`${folder}${candidate}`) : null;
      } catch {}
      if (!file) continue;
      await this.giveFile(file);
      if (!this.ide.project) return;
      let recipe = null;
      try {
        recipe = JSON.parse(await this.ide.project().blob(file).text());
      } catch {
        return;
      }
      const definition = recipe && recipe.definition && typeof recipe.definition === "object" ? recipe.definition : {};
      const own = file.replace(/[^/]*$/, "");
      const files = [definition.icon, ...(Array.isArray(definition.kept) ? definition.kept : [])];
      for (const each of files) {
        if (typeof each !== "string" || !each || /^([a-z][a-z0-9+.-]*:|\/)/i.test(each)) continue;
        try {
          await this.giveResource(`${own}${each}`);
        } catch {}
      }
      return;
    }
  }

  // world_action interior: the IDE builds an interior from its program with
  // blitz.maplet, and the editor brings in its model, rooms, lights and start
  // (scene_interiors.bb). The program is a project path; the editor takes it
  // from the world's folder. The answer says what was built, or why not, with
  // the checks that failed, and shows the floor plan the program saved.
  async interior(input, members) {
    if (typeof input.program === "string" && input.program) {
      const named = input.program.replace(/\\/g, "/");
      const file = this.ide.projectPath ? this.ide.projectPath(named) : named;
      if (!file || !/\.bb$/i.test(file)) {
        throw new WorldToolError(`The project has no program ${input.program}: an interior's program is a .bb file ` +
          "that builds it with blitz.maplet, such as interiors/castle/castle.bb.");
      }
      await this.giveFile(file);
      const editor = this.ide.worldEditor();
      members.program = pathFromWorld(editor && editor.path, file);
    } else if (typeof input.object !== "string" || !input.object) {
      throw new WorldToolError("Name the interior's program (program), or the interior to build again (object).");
    }
    const { message, file } = await this.request("action", members, INTERIOR_MS);
    const failed = (message.checks || []).filter((check) => check && check.passed === false)
      .map((check) => `${check.name}: ${check.found}`);
    const problems = (message.problems || []).filter((problem) => typeof problem === "string");
    if (!message.ok) {
      const lines = [message.error || "The interior could not be built."];
      if (failed.length) lines.push(`Failed checks:\n${failed.map((line) => `- ${line}`).join("\n")}`);
      if (problems.length > 1) lines.push(`Problems:\n${problems.map((line) => `- ${line}`).join("\n")}`);
      // Without the compiler's progress lines, which say nothing.
      const printed = String(message.output || "").split(/\r?\n/)
        .filter((line) => line.trim() && !/^(Parsing|Generating|Translating|Assembling|Executing)\.\.\.$/.test(line.trim()));
      if (printed.length) lines.push(`What the program printed:\n${printed.join("\n")}`);
      if (message.picture) lines.push(`Its plan is ${message.picture}: build again once it is fixed.`);
      throw new WorldToolError(lines.join("\n\n"));
    }
    let text = `${message.note || "Built."} The interior is the object ${message.object} (its model is the asset ` +
      `${message.asset}), built by ${message.program}; its rooms are zones under it, with its lights and its start.`;
    if (message.warning) text += ` ${message.warning}.`;
    if ((message.pictures || []).length) text += ` Pictures it saved: ${message.pictures.join(", ")}.`;
    if (!file || !file.data || this.pictures >= MAX_PICTURES) return text;
    ++this.pictures;
    const picture = await this.ide.smallPicture(file.data, PICTURE_WIDTH);
    return [{ type: "text", text: `${text} This is ${message.picture}:` },
      { type: "image", source: { type: "base64", media_type: picture.type, data: picture.data } }];
  }

  // world_action maplet: the user shapes an interior by hand in Maplet+'s
  // editor, which the desktop IDE starts on its Maplet+ project. An interior
  // its program built is switched to the project first, as one of Blip's
  // edits (scene_interiors.bb). The online IDE has no Maplet+; the editor says
  // so.
  async maplet(input, members) {
    if (typeof input.object !== "string" || !input.object) {
      throw new WorldToolError("Name the interior to open in Maplet+ (object).");
    }
    const { message } = await this.request("action", members, MAPLET_MS);
    if (!message.ok) throw new WorldToolError(message.error || "Maplet+ did not open.");
    const switched = message.switched ? " It was switched to its Maplet+ project, which now builds it; " +
      "its old program is kept but no longer builds it." : "";
    return `${message.note || "Maplet+ is open."}${switched} The project is ${message.project}, and ${message.program} ` +
      "builds the interior from it and marks its rooms: when the user saves in Maplet+, the editor builds it again.";
  }

  // A sound file of the project, given to the editor, as its path from the
  // world's folder.
  async soundSource(path) {
    const given = typeof path === "string" && path ? path.replace(/\\/g, "/") : null;
    const file = given && this.ide.projectPath ? this.ide.projectPath(given) : given;
    if (!file || ASSET_KINDS[file.replace(/^.*\./, "").toLowerCase()] !== "sound") {
      throw new WorldToolError(`The project has no sound file ${path} (a .wav or .ogg).`);
    }
    await this.giveFile(file);
    const editor = this.ide.worldEditor();
    return pathFromWorld(editor && editor.path, file);
  }

  // soundscape's files as the editor takes them: music, a path (or none);
  // beds, [{ path, volume }]; oneShots, [{ paths, every, distance, volume }];
  // each path made its source.
  async soundscapeSources(input, members) {
    if (typeof input.music === "string" && input.music.trim()) {
      members.music = input.music.trim().toLowerCase() === "none" ? "none" : await this.soundSource(input.music.trim());
    } else if (input.music !== undefined) {
      throw new WorldToolError('music is the path of a sound file of the project, or "none".');
    }
    if (input.beds !== undefined) {
      if (!Array.isArray(input.beds) || input.beds.length > 16 || input.beds.some((bed) => !bed || typeof bed.path !== "string")) {
        throw new WorldToolError("beds is a list of up to 16 { path, volume }, each a sound file of the project.");
      }
      members.beds = [];
      for (const bed of input.beds) {
        members.beds.push({ source: await this.soundSource(bed.path), ...(bed.volume !== undefined ? { volume: bed.volume } : {}) });
      }
    }
    if (input.oneShots !== undefined) {
      if (!Array.isArray(input.oneShots) || input.oneShots.length > 16 || input.oneShots.some((set) => !set ||
          !Array.isArray(set.paths) || !set.paths.length || set.paths.length > 16)) {
        throw new WorldToolError("oneShots is a list of up to 16 { paths, every, distance, volume }, each with 1 to 16 sound " +
          "files of the project.");
      }
      members.oneShots = [];
      for (const set of input.oneShots) {
        const sources = [];
        for (const path of set.paths) sources.push(await this.soundSource(path));
        const entry = { sources };
        for (const key of ["every", "distance", "volume"]) if (set[key] !== undefined) entry[key] = set[key];
        members.oneShots.push(entry);
      }
    }
  }

  async giveFile(path) {
    const file = typeof path === "string" && this.ide.projectPath ? this.ide.projectPath(path.replace(/\\/g, "/")) : null;
    if (file && this.ide.putToolFile) await this.ide.putToolFile(file);
  }

  // The files an edit's assets name, which may be new since the editor
  // opened, given to it first, as world_action's are: an asset's own file,
  // and those of a resource kept in the world, such as a material's
  // pictures. Their paths are from the world's folder.
  async giveSources(operations) {
    const editor = this.ide.worldEditor();
    const folder = editor && editor.path ? editor.path.replace(/[^/]*$/, "") : "";
    const sources = [];
    for (const operation of operations) {
      const asset = operation.asset;
      if (!asset || typeof asset !== "object") continue;
      if (typeof asset.source === "string") sources.push(asset.source);
      const own = asset.inline && Array.isArray(asset.inline.assets) ? asset.inline.assets : [];
      for (const each of own) if (each && typeof each.source === "string") sources.push(each.source);
    }
    for (const source of sources) {
      if (!source || /^([a-z][a-z0-9+.-]*:|\/)/i.test(source)) continue;
      try {
        await this.giveResource(`${folder}${source}`);
      } catch {}
    }
  }

  // A file given to the editor with the files it names: a resource, such as
  // an animation set or a material, names its own assets' files, from its
  // folder, which may be new since the editor opened too.
  async giveResource(path) {
    await this.giveFile(path);
    const file = typeof path === "string" && this.ide.projectPath ? this.ide.projectPath(path.replace(/\\/g, "/")) : null;
    if (!file || !/\.(animset|material|particles|environment|prefab)$/i.test(file) || !this.ide.project) return;
    let resource = null;
    try {
      resource = JSON.parse(await this.ide.project().blob(file).text());
    } catch {
      return;
    }
    const folder = file.replace(/[^/]*$/, "");
    for (const asset of Array.isArray(resource && resource.assets) ? resource.assets : []) {
      const source = asset && typeof asset.source === "string" ? asset.source : "";
      if (!source || /^([a-z][a-z0-9+.-]*:|\/)/i.test(source)) continue;
      try {
        await this.giveFile(`${folder}${source}`);
      } catch {}
    }
  }

  // A model, picture or sound of the project as a new asset of the world's,
  // as Assets' Import makes one: for world_action import, and a model's card
  // (panel.js), between beginRequest and finishRequest. It is given to the
  // editor first, and added through the edit protocol with its path from the
  // world's folder (pathFromWorld): the editor's own import writes the path
  // as its platform spells it, which the browser's runtime cannot read. A
  // model with a skeleton, clips or parts keeps them (options.animated).
  // A file with the name of an asset's lost file repairs that asset instead
  // (locate-file). Returns { asset, kind, message }, message.repaired saying
  // which it did.
  async importFile(path, label) {
    const file = this.ide.projectPath ? this.ide.projectPath(path) : path;
    const kind = file && ASSET_KINDS[file.replace(/^.*\./, "").toLowerCase()];
    if (!file || !kind) throw new WorldToolError(`The project has no model, picture or sound ${path}.`);
    await this.beginRequest();
    await this.giveFile(file);
    const { message: state } = await this.request("state", { limit: 1 });
    if (!state.ok) throw new WorldToolError(state.error || "The World Editor could not list the world's assets.");
    // A file with the name of an asset's lost file is that file, imported
    // again: the asset uses it, as the Assets tab's Import does, rather than a
    // new asset that the world's objects would not use.
    const name = file.replace(/^.*\//, "").toLowerCase();
    const lost = (state.assets || []).find((asset) => asset.lost && typeof asset.source === "string" &&
      asset.source.replace(/^.*[\\/]/, "").toLowerCase() === name && (asset.kind === kind ||
        (asset.kind === "heightmap" && kind === "texture")));
    if (lost) {
      const { message } = await this.request("action", { name: "locate-file", asset: lost.id, path: file, label });
      if (!message.ok) throw new WorldToolError(`${file} could not be used for the asset ${lost.id}: ${message.error}`);
      this.labels.push(label);
      return { asset: lost.id, kind: lost.kind, message: { ...message, repaired: true } };
    }
    const taken = new Set((state.assets || []).map((asset) => asset.id));
    const base = file.replace(/^.*\//, "").replace(/\.[^.]*$/, "").toLowerCase().replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || kind;
    let id = base;
    for (let n = 2; taken.has(id); ++n) id = `${base}-${n}`;
    const editor = this.ide.worldEditor();
    const source = pathFromWorld(editor && editor.path, file);
    // A sound is one its objects can play: a trigger's playSound, a
    // collectible's, an emitter's, which name a sound definition (blitz3d.sound)
    // around the file, as the editor's Sound and soundscapes make them.
    const asset = kind === "sound" ? { id, kind: "sound-definition", loading: "required", inline: {
      format: "blitz3d.sound", version: 1, id: `${id}-${crypto.randomUUID()}`, name: file.replace(/^.*\//, "").replace(/\.[^.]*$/, ""),
      assets: [{ id: "clip", kind: "sound", source }], definition: { source: "clip" } } } :
      { id, kind, source, loading: "required" };
    if (kind === "model") asset.options = { animated: await this.animated(file) };
    const { message } = await this.request("edit", { label, operations: [{ op: "asset.add", asset }] });
    if (!message.ok) throw new WorldToolError(`${file} could not be added to the world's assets: ${message.error}`);
    this.labels.push(label);
    return { asset: id, kind, message };
  }

  // Whether a GLB model has a skeleton, animation clips or parts to keep.
  async animated(file) {
    if (!/\.glb$/i.test(file) || !this.ide.project) return false;
    try {
      const project = this.ide.project();
      const info = describeGlb(readGlb(new Uint8Array(await project.blob(file).arrayBuffer())));
      return info.bones > 0 || info.clips.length > 0 || info.meshes > 1;
    } catch {
      return false;
    }
  }

  // world_route: whether a character can walk from one place to another, on
  // the walkable map the editor's views have, and how (scene_navigation.bb).
  async route(input) {
    const members = {};
    for (const key of ["from", "to"]) {
      const place = input[key];
      if (!(typeof place === "string" && place) && !isVector(place)) throw new WorldToolError(`${key} is an object's id or a point, [x, y, z].`);
      members[key] = place;
    }
    if (input.size !== undefined) {
      if (typeof input.size !== "string") throw new WorldToolError("size is a size of character's name, such as Person.");
      members.size = input.size;
    }
    const { message } = await this.request("route", members);
    if (!message.ok) throw new WorldToolError(message.error || "The World Editor could not find a route.");
    const parts = [message.said ? message.said.replace(/[.!]?$/, ".") : `The route is ${message.status}.`];
    if (message.status !== "none" && typeof message.length === "number") {
      parts.push(`Length ${number(message.length)} m${typeof message.cost === "number" && Math.abs(message.cost - message.length) > 0.05 ?
        `, counting as ${number(message.cost)} m for the areas it crosses` : ""}.`);
    }
    if (Array.isArray(message.areas) && message.areas.length) parts.push(`It crosses ${message.areas.join(", ")}.`);
    if (Array.isArray(message.links) && message.links.length) {
      parts.push(`It takes ${message.links.map((link) => `${link.link || "a walk link"}${link.kind ? ` (${link.kind})` : ""}`).join(", ")}.`);
    }
    if (message.status === "partial" && Array.isArray(message.stop)) {
      parts.push(`It stops at ${vector(message.stop)}, ${number(message.stopsShort || 0)} m short of ${vector(message.to)}.`);
    }
    if (Array.isArray(message.corners) && message.corners.length) {
      parts.push(`Corners: ${message.corners.slice(0, 40).map(vector).join(", ")}${message.corners.length > 40 ? ", ..." : ""}.`);
    }
    parts.push("The editor's views show it.");
    return parts.join(" ");
  }

  async probe(input) {
    const members = {};
    if (input.above !== undefined) {
      if (!isVector(input.above)) throw new WorldToolError("above is a point, [x, y, z].");
      members.above = input.above;
    } else {
      if (!isVector(input.from) || !isVector(input.direction)) throw new WorldToolError("Give from and direction, or above, each [x, y, z].");
      members.from = input.from;
      members.direction = input.direction;
    }
    const { message } = await this.request("probe", members);
    if (!message.ok) throw new WorldToolError(message.error || "The World Editor could not probe.");
    // Over a terrain: its ground's height there, under whatever stands on it.
    let ground = typeof message.ground === "number" ? ` The ground of ${message.groundOf} is at y ${number(message.ground)} ` +
      "there." : "";
    // The terrain's regions there, by map.
    if (Array.isArray(message.regions)) {
      ground += message.regions.length ? ` It is in ${message.regions.map((entry) => `${entry.region} (${entry.map} map)`)
        .join(", ")}.` : " It is in none of the terrain's regions.";
    }
    if (!message.hit) return `The ray hit nothing.${ground}`;
    return `It hit ${message.object} ("${message.name}") at ${vector(message.point)}, where the surface faces ` +
      `${vector(message.normal)}.${ground}`;
  }

  async look(input) {
    if (this.pictures >= MAX_PICTURES) throw new WorldToolError(`That is ${MAX_PICTURES} pictures for this request already: describe what you see from world_state instead.`);
    const members = {};
    if (input.from !== undefined) {
      if (!isVector(input.from)) throw new WorldToolError("from is a point, [x, y, z].");
      members.from = input.from;
    }
    if (input.target !== undefined) {
      if (!isVector(input.target)) throw new WorldToolError("target is a point, [x, y, z].");
      members.target = input.target;
    }
    if (input.labels === false) members.labels = false;
    if (input.view === "player") members.view = "player";
    if (input.view === "top") {
      if (members.from) throw new WorldToolError("The top view looks straight down at target: give target, not from.");
      members.view = "top";
      if (input.size !== undefined) members.size = this.topSize(input.size);
    } else if (input.size !== undefined) {
      throw new WorldToolError("size is for the top view (view top).");
    }
    const { message, file } = await this.request("look", members);
    if (!message.ok) throw new WorldToolError(message.error || "The World Editor could not take a picture.");
    if (!file || !file.data) throw new WorldToolError("The World Editor sent no picture.");
    ++this.pictures;
    const camera = message.camera || {};
    const names = input.labels === false ? " Names are left off." :
      " Each object's name is written where it shows, unless another's is there already.";
    let text;
    if (message.view === "top" && message.area) {
      text = `From straight above: ${areaText(message.area)}.${names}`;
    } else {
      const whose = { from: "From the point asked for", player: "Through the playing user's eyes (a ring marks what they point at)" };
      text = `${whose[message.view] || "The user's view in the editor"}: the camera at ${vector(camera.position)}, turned ` +
        `${vector(camera.rotation)}.${names}`;
    }
    const picture = await this.ide.smallPicture(file.data, PICTURE_WIDTH);
    return [{ type: "text", text }, { type: "image", source: { type: "base64", media_type: picture.type, data: picture.data } }];
  }

  topSize(size) {
    if (typeof size !== "number" || !(size > 0.01) || size > 100000) {
      throw new WorldToolError("size is how much of the world the top view shows across, more than 0.");
    }
    return size;
  }

  // The world from straight above, for the sketch pad (guides.js), which
  // asks the editor itself, without Claude: { png (its bytes), area (the
  // world x and z at its edges, { x0, z0, x1, z1 }), width, height, size,
  // target }. target and size are the editor's focus point and view's
  // width when not given.
  async topView({ target = null, size = null } = {}) {
    const members = { view: "top" };
    if (target) members.target = target;
    if (size !== null) members.size = this.topSize(size);
    const { message, file } = await this.request("look", members);
    if (!message.ok) throw new WorldToolError(message.error || "The World Editor could not take a picture.");
    if (!file || !file.data || !message.area) throw new WorldToolError("The World Editor sent no picture from above.");
    const { x0, z0, x1, z1 } = message.area;
    return { png: file.data, area: message.area, width: message.width, height: message.height, size: Math.abs(x1 - x0),
      target: isVector(message.target) ? message.target : target || [(x0 + x1) / 2, 0, (z0 + z1) / 2] };
  }

  // The player the editor's Test started, linked to it
  // (docs/BLIP_VR_EDITOR_DESIGN.md): outlines, taking the user somewhere,
  // physics, and a word spoken aloud (ide.say, where the IDE can speak).
  async play(input) {
    const names = ["highlight", "move_user", "play_physics", "stop_physics", "say"];
    if (!names.includes(input.name)) throw new WorldToolError(`name is one of ${names.join(", ")}.`);
    if (input.name === "say") {
      if (typeof input.text !== "string" || !input.text.trim()) throw new WorldToolError("Give text, one short sentence to say.");
      if (!this.ide.say) throw new WorldToolError("Nobody is playing the world here, so there is no one to say it to: write it in your reply.");
      return await this.ide.say(input.text.trim().slice(0, 200));
    }
    const members = { name: input.name };
    if (input.name === "highlight") {
      if (!Array.isArray(input.ids) || !input.ids.length || input.ids.some((id) => typeof id !== "string")) {
        throw new WorldToolError("ids is a list of objects' ids.");
      }
      members.ids = input.ids.slice(0, 200);
      if (input.seconds !== undefined) members.seconds = input.seconds;
    } else if (input.name === "move_user") {
      if (typeof input.object === "string" && input.object) members.object = input.object;
      else if (isVector(input.point)) members.point = input.point;
      else throw new WorldToolError("Give object (an id) or point ([x, y, z]).");
      if (typeof input.yaw === "number" && Number.isFinite(input.yaw)) members.yaw = input.yaw;
    }
    const { message } = await this.request("action", members);
    if (!message.ok) throw new WorldToolError(message.error || "The World Editor could not do that.");
    switch (input.name) {
      case "highlight": return `Outlined ${plural(members.ids.length, "object")} for a moment.`;
      case "move_user": return `The user is now standing at ${vector(message.position)}, facing yaw ${number(message.yaw)}.`;
      case "play_physics": return "Physics is running in the player, from the world as authored. It stops at the next change, or with stop_physics.";
      default: return "Physics has stopped, and everything is back where it was.";
    }
  }

  async openEditor(input) {
    if (input.path !== undefined && (typeof input.path !== "string" || !/\.world$/i.test(input.path))) {
      throw new WorldToolError("path is a .world file of the project, such as levels/one.world, or none for a new world.");
    }
    if (this.open()) return `The World Editor is open already, on ${this.ide.worldEditor().path || "a new world"}.`;
    const result = await this.ide.openWorldEditor(input.path || null);
    if (!result.opened) throw new WorldToolError(result.reason || "The World Editor could not open.");
    // It answers once it has started, which takes some seconds.
    this.deaf = null;
    for (let attempt = 0; attempt < 30; ++attempt) {
      try {
        await this.reachable(2000);
        await this.beginRequest();
        return `The World Editor is open on ${input.path || "a new world"}. Look at it with world_state.`;
      } catch (error) {
        if (!(error instanceof WorldToolError) || error.message !== DEAF) throw error;
        this.deaf = null;
      }
    }
    throw new WorldToolError("The World Editor opened, but does not answer yet.");
  }

  // Shows what a step made or changed and is still in the world: selects and
  // frames it.
  async show(step) {
    const wanted = [...step.made, ...step.changed];
    if (!wanted.length) return;
    const { message } = await this.request("state", { ids: wanted.slice(0, 500), limit: 500 });
    const ids = (message.objects || []).map((object) => object.id);
    if (!ids.length) throw new WorldToolError("Those objects are no longer in the world.");
    const { message: framed } = await this.request("action", { name: "frame", ids });
    if (!framed.ok) throw new WorldToolError(framed.error || "The World Editor could not show them.");
  }

  // Takes back one of Blip's steps, of this run of the editor only. Returns
  // { ok, error }.
  async undo(step) {
    const members = { step: step.step };
    if (step.session) members.session = step.session;
    const { message } = await this.request("undo", members);
    return { ok: !!message.ok, error: message.error || "" };
  }
}
